/**
 * useSIP - Enhanced SIP/WebRTC Hook for ContinuumCX
 * 
 * Features:
 * - SIP Registration with failover
 * - Inbound/Outbound calls
 * - Hold/Resume, Mute/Unmute
 * - Blind & Attended Transfer
 * - 3-Way Conference
 * - DTMF
 * - Audio device management
 * - Call quality metrics
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import JsSIP from 'jssip';
import CONFIG from '../config';
import { installSDPMinifier } from '../utils/sdpMinify';
import { playRingback, playIncomingRing } from '../utils/ringback';
import { softphoneApi, SOFTPHONE_SESSION_ID } from '../api/client';

// Enable verbose JsSIP logging so we can see full SIP message bodies in
// the browser console while debugging SDP / call-flow issues.
if (typeof window !== 'undefined') {
  try {
    JsSIP.debug.enable('JsSIP:*');
  } catch (e) {
    // ignore - some bundlers strip the debug helper
  }
}

// COSMETIC (2026-07-29): merge "failure" reasons that are actually benign
// backend leg-detection races. The ConfBridge conference still establishes
// correctly when these fire (confirmed by agents on the inbound->911
// conference flow), so we must NOT surface them to the agent as a red
// "Merge failed" banner. They stay in the console for debugging. Any OTHER
// reason (a real merge failure) is still shown.
function _isBenignMergeReason(reason) {
  if (!reason || typeof reason !== 'string') return false;
  const r = reason.toLowerCase();
  return r.includes('missing_pstn_legs') || r.includes('missing_pstn_partners');
}

// Connection states
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  REGISTERED: 'registered',
  ERROR: 'error'
};

// Call states
export const CallState = {
  IDLE: 'idle',
  INCOMING: 'incoming',
  RINGING: 'ringing',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  HOLDING: 'holding',
  HELD: 'held',           // Remote party put us on hold
  TRANSFERRING: 'transferring',
  CONFERENCING: 'conferencing',
  ENDED: 'ended'
};

// Transfer types
export const TransferType = {
  BLIND: 'blind',
  ATTENDED: 'attended'
};

export function useSIP() {
  // Connection state
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [currentServer, setCurrentServer] = useState(null);
  const [error, setError] = useState(null);
  
  // Call state
  const [callState, setCallState] = useState(CallState.IDLE);
  const [callInfo, setCallInfo] = useState(null);
  // Keep a ref of the current callInfo so park() can snapshot it into
  // the held-calls tray without stale-closure worries.
  const callInfoRef = useRef(null);
  useEffect(() => { callInfoRef.current = callInfo; }, [callInfo]);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLocalHold, setIsLocalHold] = useState(false);
  const [isRemoteHold, setIsRemoteHold] = useState(false);
  
  // Transfer state
  const [transferState, setTransferState] = useState(null);
  const [consultSession, setConsultSession] = useState(null);
  const [conferenceRoom, setConferenceRoom] = useState(null);
  const [conferenceMerged, setConferenceMerged] = useState(false);

  // ---------------------------------------------------------------------
  // Multi-call MC-1 (2026-07-02) — held-calls "tray".
  //
  // We keep `session.current` as the SOLE active call so all existing
  // transfer / conference / hold / DTMF / audio-wiring paths keep working
  // unmodified. Additional calls live in `heldCalls`: an array of
  // { id, jsSipSession, callInfo, direction, heldAt } entries.
  //
  // API:
  //   parkCurrent()          hold current session, move to heldCalls,
  //                          clear session.current so a new call can start.
  //                          Returns { id } or null if nothing active.
  //   resumeHeld(id)         park current (if any), promote held to active,
  //                          unhold, re-attach audio.
  //   hangupHeld(id)         terminate the held session, drop from list.
  //   startNewCall()         convenience: parkCurrent then set state IDLE
  //                          so the softphone UI can show its dialer.
  //
  // Hard cap for MC-1: 1 active + 1 held. 2nd incoming call while at cap
  // is auto-rejected 486 Busy.
  // ---------------------------------------------------------------------
  const [heldCalls, setHeldCalls] = useState([]);
  const heldCallsRef = useRef([]);
  useEffect(() => { heldCallsRef.current = heldCalls; }, [heldCalls]);
  // 2nd incoming call while the agent is already on a call. This is the
  // "ringing waiter" — shown as a small banner. Accepting parks the
  // active call and promotes this one.
  const [parkedIncoming, setParkedIncoming] = useState(null);
  // MC-2 (2026-07-02): raised cap 2 → 3. Agent can now hold up to 2 calls
  // while on a third active one.
  const MAX_CONCURRENT_CALLS = 3;

  // Snapshot of the CallInfo bag we render in the UI, refreshed on state
  // change. We stash a copy here for the held-tray display.
  const currentCallInfoRef = useRef(null);
  // 2026-08-03: merge-window guard. A conference MERGE tears down session A
  // (the queue leg) and Originates a NEW conf-join leg that auto-answers ~1-2s
  // later. Only during that narrow window should an accepted session STAY in
  // CONFERENCING; and only during that window should cleanupCall() PRESERVE the
  // conference flags. Outside the window, conference state MUST reset -- else a
  // conference that ended abnormally (caller hangup / End All / tab drop /
  // network) leaves conferenceMergedRef stuck true, and the NEXT plain inbound
  // call is forced into CONFERENCING (conference panel, no Hold button) = the
  // "single caller came in as a conference" bug (61059, 2026-08-03).
  const conferenceMergeWindowUntil = useRef(0);
  const _inMergeWindow = () => Date.now() < conferenceMergeWindowUntil.current;
  // Ref mirror of conferenceMerged so handlers registered before the merge
  // can read the live value (avoids stale-closure bug).
  const conferenceMergedRef = useRef(false);
  
  // Audio devices
  const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] });
  const [selectedInput, setSelectedInput] = useState(null);
  const [selectedOutput, setSelectedOutput] = useState(null);
  
  // Call quality
  const [callQuality, setCallQuality] = useState(null);
  
  // Refs
  const ua = useRef(null);
  const session = useRef(null);
  // Stable id for the currently-active session (used to correlate with the
  // held-calls tray after park/resume).
  const currentSessionIdRef = useRef(null);
  const sseSource = useRef(null);  // backend events: incoming-queue-call, etc.
  const durationTimer = useRef(null);
  const qualityTimer = useRef(null);
  const localStream = useRef(null);
  const remoteAudio = useRef(null);
  const serverIndex = useRef(0);
  // Tracks intentional logout/disconnect by the user (clicked Log Out).
  // When true, suppress registrationFailed retries + error toasts so we don't
  // spam the agent after they deliberately signed out. Reset on reconnect.
  const userInitiatedDisconnect = useRef(false);
  // Remember the last creds so the Reconnect button can re-run connect() without
  // requiring the parent to re-fetch the SIP bundle.
  const lastCreds = useRef(null);
  const [didUserLogout, setDidUserLogout] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════
  // AUDIO DEVICE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════
  
  const refreshAudioDevices = useCallback(async () => {
    try {
      // We need mic permission to see device LABELS. Without it the
      // enumerateDevices() returns devices with empty .label strings.
      // Request once, stop the stream immediately to avoid mic LED staying on.
      let permissionStream = null;
      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (permErr) {
        // Permission denied - we still get devices but with blank labels.
        console.warn('[CX] Mic permission denied; device labels will be blank:', permErr.message);
      } finally {
        if (permissionStream) {
          permissionStream.getTracks().forEach(t => t.stop());
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(d => d.kind === 'audioinput');
      const outputs = devices.filter(d => d.kind === 'audiooutput');

      setAudioDevices({ inputs, outputs });

      // Set defaults if not set
      if (!selectedInput && inputs.length > 0) {
        setSelectedInput(inputs[0].deviceId);
      }
      if (!selectedOutput && outputs.length > 0) {
        setSelectedOutput(outputs[0].deviceId);
      }
    } catch (err) {
      console.error('[CX] Failed to enumerate audio devices:', err);
    }
  }, [selectedInput, selectedOutput]);
  
  // Listen for device changes
  useEffect(() => {
    refreshAudioDevices();
    navigator.mediaDevices.addEventListener('devicechange', refreshAudioDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', refreshAudioDevices);
    };
  }, [refreshAudioDevices]);
  
  // Change audio input device
  const setInputDevice = useCallback(async (deviceId) => {
    setSelectedInput(deviceId);
    
    // If in call, switch the track
    if (session.current && localStream.current) {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } }
        });
        const newTrack = newStream.getAudioTracks()[0];
        const sender = session.current.connection
          ?.getSenders()
          ?.find(s => s.track?.kind === 'audio');
        
        if (sender) {
          await sender.replaceTrack(newTrack);
          localStream.current.getAudioTracks().forEach(t => t.stop());
          localStream.current = newStream;
        }
      } catch (err) {
        console.error('Failed to switch input device:', err);
      }
    }
  }, []);
  
  // Change audio output device
  const setOutputDevice = useCallback(async (deviceId) => {
    setSelectedOutput(deviceId);
    
    if (remoteAudio.current?.setSinkId) {
      try {
        await remoteAudio.current.setSinkId(deviceId);
      } catch (err) {
        console.error('Failed to switch output device:', err);
      }
    }
  }, []);
  
  // ═══════════════════════════════════════════════════════════════
  // CONNECTION MANAGEMENT
  // ═══════════════════════════════════════════════════════════════
  
  const connect = useCallback((extension, password, wssUriOverride) => {
    // Reset the intentional-logout flag so any failure during this fresh
    // connect attempt is surfaced to the user.
    userInitiatedDisconnect.current = false;
    setDidUserLogout(false);
    lastCreds.current = { extension, password, wssUriOverride };
    setConnectionState(ConnectionState.CONNECTING);
    setError(null);

    // Phase H.1: prefer an explicit wssUri (from the credentials bundle,
    // ordered by the currently active cluster) over the static CONFIG list.
    // Falls back to CONFIG.servers if no override was passed (legacy path).
    let server;
    if (wssUriOverride) {
      server = {
        name: 'Active cluster (from bundle)',
        wsUri: wssUriOverride,
        sipDomain: CONFIG.servers[0]?.sipDomain || 'continuumgbl.com',
      };
    } else {
      server = CONFIG.servers[serverIndex.current];
    }
    setCurrentServer(server);

    const socket = new JsSIP.WebSocketInterface(server.wsUri);

    const configuration = {
      sockets: [socket],
      uri: `sip:${extension}@${server.sipDomain}`,
      password: password,
      display_name: extension,
      register: true,
      register_expires: CONFIG.registration.expires,
      session_timers: false,
      use_preloaded_route: false,
      // Prevent SDP from blocking on STUN responses that may never arrive
      // (corp / home FW often drops outbound UDP/19302). Without this, ICE
      // gathering waits up to 40s before sending the INVITE.
      // 2 seconds is plenty for host + reflexive candidates.
      // Reference: https://jssip.net/documentation/api/ua/
      // (option name varies between versions; both work safely if set)
    };
    
    ua.current = new JsSIP.UA(configuration);
    
    // UA event handlers
    ua.current.on('connecting', () => {
      setConnectionState(ConnectionState.CONNECTING);
    });
    
    ua.current.on('connected', () => {
      setConnectionState(ConnectionState.CONNECTED);
    });
    
    // Phase 2.5 v2: open Server-Sent Events stream for backend-pushed signals
    // (auto-answer, future wallboard updates, etc.). The endpoint is gated
    // by the same portal cookie we use everywhere else, so credentials follow.
    //
    // Resilient open: wrap in a function we can call again. The browser's
    // built-in EventSource auto-reconnect is unreliable across long-lived
    // tabs (suspended tabs, hibernation, server restarts). We re-open
    // explicitly on `onerror` after a short backoff so the auto-answer
    // signal path stays alive.
    const openSse = () => {
      try {
        if (sseSource.current) {
          try { sseSource.current.close(); } catch (e) {}
          sseSource.current = null;
        }
        // Include the per-tab softphone session id so the backend can enforce
        // a single active session per agent (last connect wins). EventSource
        // can't set headers, so we pass it as a query param.
        const _sid = SOFTPHONE_SESSION_ID || '';
        const _sseUrl = '/api/softphone/events' + (_sid ? ('?sid=' + encodeURIComponent(_sid)) : '');
        const es = new EventSource(_sseUrl, { withCredentials: true });
        sseSource.current = es;
        console.log('[CX-SSE] EventSource opened', _sid ? ('sid=' + _sid) : '');

        // Screen-recording engagement RESET: the backend reaper force-closed a
        // stuck 'recording' row for this agent. The client engagement state is
        // likely wedged (swallowing new calls -> no recording). Reset it.
        es.addEventListener('screen-recording-reset', (e) => {
          try {
            const sr = window.__cxScreenRec;
            console.warn('[CX-SR] backend requested engagement reset (stuck recording reaped)');
            if (sr && sr.resetEngagement) sr.resetEngagement();
          } catch (err) {
            console.warn('[CX-SR] engagement reset failed', err);
          }
        });

        // Single-active-session: a NEWER tab claimed this agent. Go passive.
        es.addEventListener('session-superseded', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.warn('[CX-SSE] session-superseded — another tab took over', p);
            window.dispatchEvent(new CustomEvent('cx-session-superseded', { detail: p }));
          } catch (err) {
            window.dispatchEvent(new CustomEvent('cx-session-superseded', { detail: {} }));
          }
        });

        es.addEventListener('connected', (e) => {
          console.log('[CX-SSE] connected', e.data);
        });
        // 2026-08-10 N-party conference: backend recomputes the room's
        // non-agent party count on every ConfbridgeJoin/Leave + publishes
        // this so the softphone can relabel Conference<->survivor-number and
        // enable/disable controls (FOUR-LINE-CONFERENCE-DESIGN.md).
        es.addEventListener('conference-participants', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] conference-participants', p);
            window.dispatchEvent(new CustomEvent('cx-conference-participants', { detail: p }));
          } catch (err) {
            console.warn('[CX-SSE] conference-participants parse failed', err);
          }
        });
        es.addEventListener('incoming-queue-call', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] incoming-queue-call', p);
            if (p && p.auto_answer) {
              window.__cxNextAutoAnswerUntil = Date.now() + 10_000;
              console.log('[CX-SSE] armed auto-answer for next 10s');
            }
            // Stash Asterisk uniqueid (10s window) so the per-call screen
            // recorder can start with the right call_id when the SIP INVITE
            // arrives moments later. Phase 2.1 (2026-06-22).
            //
            // 2026-07-02 (Phase 2.3): ALWAYS clear any prior pending value
            // before stashing the new one. Fixes the 2nd-inbound-call bug
            // where the previous stash's window expired but a stale value
            // was still present, or the value was consumed by the first
            // call and never repopulated. Clearing first + always re-arming
            // the timer guarantees each SSE-notified call gets a fresh window.
            window.__cxPendingScreenRecCallId = null;
            window.__cxPendingScreenRecCallIdUntil = 0;
            if (p && p.uniqueid) {
              window.__cxPendingScreenRecCallId = p.uniqueid;
              window.__cxPendingScreenRecCallIdUntil = Date.now() + 10_000;
              console.log('[CX-SSE] stashed uniqueid for screen-rec:', p.uniqueid);
            }
          } catch (err) {
            console.warn('[CX-SSE] event parse failed', err);
          }
        });
        es.addEventListener('outbound-call-started', (e) => {
          // Phase 2.2 v2 (2026-07-02): backend AMI subscriber caught a
          // Newexten event for this agent's outbound leg landing in
          // [cx-outbound-pstn]. We do TWO things:
          //   1. Stash the Asterisk uniqueid for any future startRecording
          //      that hasn't fired yet (rare — SSE usually arrives AFTER).
          //   2. If a screen recording is ALREADY in progress (typical
          //      case — startRecording fired within ms of ua.call()), tell
          //      the backend to swap its call_id from the SIP Call-ID
          //      placeholder to this authoritative Asterisk uniqueid.
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] outbound-call-started', p);
            if (p && p.uniqueid) {
              window.__cxPendingOutboundCallId = p.uniqueid;
              window.__cxPendingOutboundCallIdUntil = Date.now() + 10_000;
              // Rebind any active recording so voice↔screen linking works.
              const sr = window.__cxScreenRec;
              if (sr && sr.rebindCallId) {
                sr.rebindCallId(p.uniqueid).catch(() => {});
              }
            }
          } catch (err) {
            console.warn('[CX-SSE] outbound-start parse failed', err);
          }
        });
        es.addEventListener('queue-call-completed', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] queue-call-completed', p);
            window.dispatchEvent(new CustomEvent('cx-queue-call-completed', { detail: p }));
          } catch (err) {
            console.warn('[CX-SSE] completed event parse failed', err);
          }
        });
        es.addEventListener('force-paused', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] force-paused', p);
            window.dispatchEvent(new CustomEvent('cx-force-paused', { detail: p }));
          } catch (err) {
            console.warn('[CX-SSE] force-paused parse failed', err);
          }
        });
        // 2026-07-27 COMPLIANCE auto-trigger: server detected the agent is on
        // a live call with NO active screen recording -> tell the browser to
        // start recording NOW. Deterministic recovery of a missed recording
        // on an in-progress call.
        es.addEventListener('force-screen-record', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] force-screen-record', p);
            const sr = window.__cxScreenRec;
            if (!sr) return;
            const ext = (lastCreds.current && lastCreds.current.extension) || null;
            // 2026-08-17 FIX 1: route the recovery trigger through serverStart too
            // so it never double-starts against an in-flight ('preparing') start.
            if (sr.serverStart) {
              console.log('[CX-SR] force-screen-record: -> serverStart (recovery)', p.uniqueid);
              sr.serverStart({ uniqueid: p.uniqueid || null, agentExtension: ext })
                .catch((err) => console.warn('[CX-SR] force-screen-record serverStart failed', err));
            } else {
              const st = sr.getState ? sr.getState() : null;
              if (st === 'recording' || st === 'preparing') return;
              sr.startRecording({ callId: p.uniqueid || null, agentExtension: ext })
                .catch((err) => console.warn('[CX-SR] force-screen-record start failed', err));
            }
          } catch (err) {
            console.warn('[CX-SSE] force-screen-record parse failed', err);
          }
        });
        // 2026-08-11 SERVER-AUTHORITATIVE screen-record triggers. The backend
        // AMI subscriber decides start/stop from the actual call lifecycle
        // (AgentConnect inbound / Newexten outbound / AgentComplete / Hangup),
        // so the browser is just an executor. This fixes the OUTBOUND coverage
        // gap (no longer depends on agent_live_state flipping) and the
        // backgrounded-tab race. Idempotent + coexists with the recovery
        // watchdog (force-screen-record) and the client's own engagement model.
        es.addEventListener('screen-record-start', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] screen-record-start', p);
            const sr = window.__cxScreenRec;
            if (!sr) return;
            const ext = (lastCreds.current && lastCreds.current.extension) || null;
            // 2026-08-17 FIX 1: route through the coordinator-owned serverStart,
            // which DEBOUNCES against an already-active or in-flight ('preparing')
            // start (rebinds the uniqueid) instead of firing a competing
            // startRecording() that races the browser's own legLive start. This
            // is the fix for the outbound double-start race (64% outbound gap).
            if (sr.serverStart) {
              console.log('[CX-SR] screen-record-start: -> serverStart (server trigger)', p.uniqueid);
              sr.serverStart({ uniqueid: p.uniqueid || null, agentExtension: ext })
                .catch((err) => console.warn('[CX-SR] screen-record-start serverStart failed', err));
            } else {
              // Fallback for an old bundle that lacks serverStart.
              const st = sr.getState ? sr.getState() : null;
              if (st === 'recording' || st === 'preparing') {
                if (p.uniqueid && sr.rebindCallId) sr.rebindCallId(p.uniqueid).catch(() => {});
                return;
              }
              sr.startRecording({ callId: p.uniqueid || null, agentExtension: ext })
                .catch((err) => console.warn('[CX-SR] screen-record-start failed', err));
            }
          } catch (err) {
            console.warn('[CX-SSE] screen-record-start parse failed', err);
          }
        });
        es.addEventListener('screen-record-stop', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] screen-record-stop', p);
            const sr = window.__cxScreenRec;
            if (!sr) return;
            const st = sr.getState ? sr.getState() : null;
            if (st !== 'recording') {
              console.log('[CX-SR] screen-record-stop: not recording, ignoring');
              return;
            }
            // stopRecording already applies the engagement wrap tail before
            // finalizing + committing the upload.
            console.log('[CX-SR] screen-record-stop: stopping (server trigger)');
            if (sr.stopRecording) {
              sr.stopRecording().catch((err) => console.warn('[CX-SR] screen-record-stop failed', err));
            }
          } catch (err) {
            console.warn('[CX-SSE] screen-record-stop parse failed', err);
          }
        });
        es.addEventListener('force-logout', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] force-logout', p);
            window.dispatchEvent(new CustomEvent('cx-force-logout', { detail: p }));
          } catch (err) {
            console.warn('[CX-SSE] force-logout parse failed', err);
          }
        });
        // Phase H.1: cluster failover. Softphone listens for this window
        // event, re-fetches /softphone/credentials, and calls switchCluster()
        // to re-REGISTER against the new active Kamailio.
        es.addEventListener('cluster.changed', (e) => {
          try {
            const p = JSON.parse(e.data);
            console.log('[CX-SSE] cluster.changed', p);
            window.dispatchEvent(new CustomEvent('cx-cluster-changed', { detail: p }));
          } catch (err) {
            console.warn('[CX-SSE] cluster.changed parse failed', err);
          }
        });
        // SSE 'keepalive' is a comment-only line every 25 sec from backend;
        // EventSource swallows comments. We still treat the bare `open` event
        // as proof of life.
        es.onopen = () => {
          console.log('[CX-SSE] open event (channel live)');
        };
        es.onerror = (err) => {
          // EventSource.readyState: 0=connecting, 1=open, 2=closed
          const state = es.readyState;
          console.warn('[CX-SSE] error, readyState=', state, err);
          if (state === 2) {
            // Closed - browser gave up. Reopen ourselves after 2s.
            console.warn('[CX-SSE] closed - reopening in 2s');
            setTimeout(openSse, 2000);
          }
          // readyState 0/1 means browser is still trying or live - leave it alone
        };
      } catch (err) {
        console.warn('[CX-SSE] could not start EventSource', err);
        setTimeout(openSse, 5000);
      }
    };
    openSse();

    // Periodic watchdog: if the SSE channel has been silent for > 90 sec,
    // assume it's stuck and force-reopen. Backend sends a comment keepalive
    // every 25 sec so a 90 sec silence is definitely a problem.
    if (sseSource.watchdog) clearInterval(sseSource.watchdog);
    sseSource.watchdog = setInterval(() => {
      const es = sseSource.current;
      if (!es) return;
      // readyState 2 = closed (browser gave up); reopen.
      if (es.readyState === 2) {
        console.warn('[CX-SSE] watchdog: channel is closed, reopening');
        openSse();
      }
    }, 30_000);

    ua.current.on('registered', () => {
      setConnectionState(ConnectionState.REGISTERED);
      serverIndex.current = 0; // Reset on successful registration
    });
    
    ua.current.on('unregistered', () => {
      setConnectionState(ConnectionState.CONNECTED);
    });
    
    ua.current.on('registrationFailed', (e) => {
      // If the user just clicked Log Out, suppress retry + error — they meant
      // to sign out. We'll still log to console for diagnostics.
      if (userInitiatedDisconnect.current) {
        console.info('[CX] registrationFailed during intentional logout — suppressed:', e.cause);
        return;
      }
      console.error('Registration failed:', e.cause);

      // Try next server (only when this is a real failure, not user logout).
      if (serverIndex.current < CONFIG.servers.length - 1) {
        serverIndex.current++;
        ua.current.stop();
        setTimeout(() => connect(extension, password), 1000);
      } else {
        setConnectionState(ConnectionState.ERROR);
        setError(`Registration failed: ${e.cause}`);
        serverIndex.current = 0;
      }
    });
    
    ua.current.on('disconnected', () => {
      setConnectionState(ConnectionState.DISCONNECTED);
    });
    
    // Incoming call handler + SDP minifier for ALL sessions (in+out)
    ua.current.on('newRTCSession', (e) => {
      // Strip noisy SDP attributes before the offer/answer hits the wire.
      // We do this for every RTCSession - inbound, outbound, consult.
      installSDPMinifier(e.session);
      if (e.originator === 'remote') {
        handleIncomingCall(e.session);
      }
    });
    
    ua.current.start();
  }, []);
  
  // Internal teardown shared by disconnect() and the cleanup useEffect.
  // `intentional=true` is set by user-clicked Log Out; intentional=false is the
  // unmount path (component being torn down for routing/page reasons).
  const _teardown = useCallback((intentional) => {
    if (intentional) {
      userInitiatedDisconnect.current = true;
      setDidUserLogout(true);
      setError(null);  // clear any stale error so we don't show "Registration failed" after logout
    }
    if (session.current) {
      try { session.current.terminate(); } catch (e) { /* ignore */ }
    }
    if (ua.current) {
      try { ua.current.stop(); } catch (e) { /* ignore */ }
    }
    if (sseSource.current) {
      try { sseSource.current.close(); } catch (e) {}
      sseSource.current = null;
    }
    if (sseSource.watchdog) {
      try { clearInterval(sseSource.watchdog); } catch (e) {}
      sseSource.watchdog = null;
    }
    setConnectionState(ConnectionState.DISCONNECTED);
    setCallState(CallState.IDLE);
    serverIndex.current = 0;
  }, []);

  const disconnect = useCallback(() => {
    console.info('[CX] disconnect() — intentional logout by user');
    _teardown(true);
  }, [_teardown]);

  // Public: clear the intentional-logout flag and re-register using last creds.
  // Used by the "Reconnect" button shown after intentional logout.
  const reconnect = useCallback(() => {
    const creds = lastCreds.current;
    if (!creds) {
      console.warn('[CX] reconnect() called but no cached creds');
      return;
    }
    setError(null);
    setDidUserLogout(false);
    userInitiatedDisconnect.current = false;
    connect(creds.extension, creds.password, creds.wssUriOverride);
  }, [connect]);

  // Phase H.1: switch JsSIP registration to a NEW Kamailio WSS endpoint.
  // Used on cluster.changed SSE events. Tears down the current UA WITHOUT
  // setting the "user logged out" flag (so we silently reconnect; agent
  // sees a brief "Reconnecting" status, not a "Signed out" wall).
  //
  // Bug-fix history (2026-06-19):
  //   v1 attempted to ua.stop() + removeAllListeners() + connect() in sequence,
  //   but the OLD UA's WSS takes ~2s to actually close. During that window
  //   the OLD UA was still firing 'disconnected' / 'unregistered' events into
  //   our React state setters - because removeAllListeners() didn't reliably
  //   clear them on this build of JsSIP.
  //
  //   v2 detached the old UA and built the new one first. But this exposed a
  //   deeper issue: JsSIP's WebSocketInterface shares the underlying socket
  //   between UAs on the same wss URL, AND oldUa.stop() eventually fires
  //   `Transport close()` on that shared socket - which kills the NEW UA's
  //   transport 2 seconds after the new REGISTER succeeded.
  //
  //   v3 (this version) stops the old UA FIRST, waits for its WebSocket to
  //   actually close (via its 'disconnected' event), then builds the new UA
  //   on a fresh socket. Agent sees ~2-3s of "Reconnecting" but the new UA
  //   doesn't lose its transport mid-stream.
  const switchCluster = useCallback((newWssUri, extension, password) => {
    console.info('[CX] switchCluster() — v3 (stop-then-connect)', newWssUri);
    const oldUa = ua.current;

    const finishSwitch = () => {
      console.info('[CX] switchCluster: old UA fully closed, building new UA');
      ua.current = null;
      session.current = null;
      // Now safe to build the new UA on a fresh socket.
      connect(extension, password, newWssUri);
    };

    if (!oldUa) {
      finishSwitch();
      return;
    }

    // Step 1: silence the old UA's listeners so they can't run setState.
    // We keep ua.current pointing at oldUa until we're done so any stray
    // handler that DOES fire sees the same identity (no "ghost" UA).
    const events = [
      'newRTCSession', 'registered', 'unregistered',
      'registrationFailed', 'connecting', 'connected', 'disconnected',
    ];
    for (const ev of events) {
      try { oldUa.removeAllListeners?.(ev); } catch (_e) { /* ignore */ }
    }
    try { oldUa.on = () => {}; } catch (_e) { /* ignore */ }

    // Step 2: install a one-shot listener that fires when the underlying
    // WebSocket actually closes. This is when we know it's safe to build
    // the new UA without it inheriting/sharing the old socket.
    let finished = false;
    const onDisconnected = () => {
      if (finished) return;
      finished = true;
      try { oldUa.removeListener?.('disconnected', onDisconnected); } catch (_e) {}
      finishSwitch();
    };
    // We bypass the `oldUa.on = () => {}` stub by going through the original
    // EventEmitter prototype method. If neither works, the timeout below covers us.
    try {
      const origOn = Object.getPrototypeOf(oldUa).on;
      if (typeof origOn === 'function') {
        origOn.call(oldUa, 'disconnected', onDisconnected);
      }
    } catch (_e) { /* fallback to timeout */ }

    // Step 3: fire stop(). This triggers UNREGISTER + WS close async.
    try { oldUa.stop(); } catch (err) {
      console.warn('[CX] switchCluster: oldUa.stop() threw, building new UA anyway', err);
      finishSwitch();
      return;
    }

    // Safety net: if the 'disconnected' event never fires (shouldn't happen
    // but JsSIP versions vary), force the switch after 3 seconds.
    setTimeout(() => {
      if (!finished) {
        console.warn('[CX] switchCluster: oldUa never emitted disconnected; forcing switch');
        onDisconnected();
      }
    }, 3000);
  }, [connect]);
  
  // ═══════════════════════════════════════════════════════════════
  // CALL HANDLING
  // ═══════════════════════════════════════════════════════════════
  
  const handleIncomingCall = (incomingSession) => {
    // ---- MC-1 (multi-call) guard ------------------------------------
    // If we're already on a call, this is a SECOND inbound call. Two
    // policies:
    //   1. If we're at the hard cap (active + held == MAX), reject with
    //      486 Busy Here — the caller's carrier / queue handles it.
    //   2. Otherwise DO NOT auto-answer even if the header/flag says so.
    //      The agent must consciously accept the 2nd call, which will
    //      park the current one.
    const hasActive = !!session.current && callState !== CallState.IDLE
      && callState !== CallState.ENDED;
    const heldCount = heldCallsRef.current.length;
    if (hasActive && heldCount + 1 >= MAX_CONCURRENT_CALLS) {
      // Cap reached — politely refuse.
      console.log('[CX] 2nd incoming call while at cap — rejecting 486 Busy');
      try {
        incomingSession.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      } catch (err) {
        console.warn('[CX] failed to reject 2nd incoming call', err);
      }
      return;
    }
    if (hasActive) {
      // 2nd call, capacity available — hand it to the parked-incoming
      // ringer path so the agent can consciously accept (which will park
      // the active call first).
      console.log('[CX] 2nd incoming call while active — queuing as PARKED_INCOMING');
      setParkedIncoming({
        id: `pi-${Date.now()}`,
        session: incomingSession,
        callInfo: {
          direction: 'inbound',
          remoteNumber: incomingSession.remote_identity?.uri?.user || '',
          remoteName: incomingSession.remote_identity?.display_name
            || incomingSession.remote_identity?.uri?.user || '',
          startTime: null,
        },
        arrivedAt: Date.now(),
      });
      // Wire a listener so if the caller gives up before we accept, we
      // clean up the parked-incoming slot.
      try {
        incomingSession.on('failed', () => setParkedIncoming(null));
        incomingSession.on('ended',  () => setParkedIncoming(null));
      } catch (_) { /* noop */ }
      // Play the ringer discreetly (short blip) but do not open the
      // full incoming-call UI — that would swap `session.current`.
      try { playRingtone(); } catch (_) {}
      return;
    }
    // ---- (single-call path, existing behavior) ----
    session.current = incomingSession;
    currentSessionIdRef.current = `call-${Date.now()}`;
    
    // Extract caller info
    const remoteIdentity = incomingSession.remote_identity;
    setCallInfo({
      direction: 'inbound',
      remoteNumber: remoteIdentity.uri.user,
      remoteName: remoteIdentity.display_name || remoteIdentity.uri.user,
      startTime: null
    });
    
    // Auto-answer: two signals, either one triggers.
    //   1. SIP `Alert-Info: <urn:alert:auto-answer>` header on the INVITE
    //      (set by the LOU Issabel dialplan when queues.auto_answer=true).
    //      This arrives IN the INVITE itself so there's no timing window
    //      and no dependence on the SSE channel being open.
    //   2. SSE-armed flag from backend `incoming-queue-call` event in the
    //      last 10 seconds (fallback path when the header is stripped).
    //
    // 2026-06-19: JsSIP's getHeader('Alert-Info') sometimes returns undefined
    // even when the parser populated headers['Alert-Info'] (the value is in
    // header[0].raw but the lookup miscases on some payloads). We now check
    // THREE places, in order of reliability:
    //   a) the raw INVITE text on request.data (parser stores it verbatim)
    //   b) request.headers map (iterated case-insensitively)
    //   c) getHeader() as a last resort
    let autoAnswer = false;
    try {
      // 2026-06-19: JsSIP RTCSession exposes the parsed INVITE on the
      // PRIVATE field `_request`, not on `request` (no public getter in
      // this version). Try both, preferring whichever has data.
      const req = (incomingSession && (incomingSession._request || incomingSession.request)) || null;
      let alertInfo = '';
      let callInfo = '';

      // (a) raw INVITE text - the parser stashes the original bytes on
      // request.data, which is what we WANT to scan.
      if (req && typeof req.data === 'string' && req.data.length) {
        const m1 = req.data.match(/^Alert-Info:\s*(.+)$/im);
        if (m1) alertInfo = m1[1].trim();
        const m2 = req.data.match(/^Call-Info:\s*(.+)$/im);
        if (m2) callInfo = m2[1].trim();
      }

      // (b) headers map - case-insensitive walk.
      if ((!alertInfo || !callInfo) && req && req.headers && typeof req.headers === 'object') {
        for (const hn of Object.keys(req.headers)) {
          const low = hn.toLowerCase();
          const arr = req.headers[hn];
          const raw = Array.isArray(arr) && arr[0] && (arr[0].raw || arr[0].value || '') || '';
          if (low === 'alert-info' && !alertInfo) alertInfo = String(raw);
          if (low === 'call-info'  && !callInfo)  callInfo  = String(raw);
        }
      }

      // (c) getHeader() - last resort.
      if ((!alertInfo || !callInfo) && req && req.getHeader) {
        if (!alertInfo) alertInfo = req.getHeader('Alert-Info') || '';
        if (!callInfo)  callInfo  = req.getHeader('Call-Info')  || '';
      }

      console.log('[CX-AUTO] header probe', {
        alertInfo:  alertInfo || '(empty)',
        callInfo:   callInfo  || '(empty)',
        dataLen:    (req && req.data && req.data.length) || 0,
        viaPrivate: !!(incomingSession && incomingSession._request),
      });

      if (/auto-answer|answer-after\s*=\s*0/i.test(alertInfo + ' ' + callInfo)) {
        autoAnswer = true;
        console.log('[CX-AUTO] Alert-Info header signaled auto-answer');
      }
    } catch (err) {
      console.warn('[CX-AUTO] header check failed', err);
    }
    if (!autoAnswer) {
      try {
        const now = Date.now();
        if (window.__cxNextAutoAnswerUntil && window.__cxNextAutoAnswerUntil > now) {
          autoAnswer = true;
          window.__cxNextAutoAnswerUntil = 0;
          console.log('[CX-AUTO] SSE flag signaled auto-answer');
        }
      } catch (err) {
        console.warn('[CX-AUTO] SSE flag check failed', err);
      }
    }
    console.log('[CX-AUTO] decision →', autoAnswer ? 'AUTO-ANSWER' : 'manual');

    setCallState(CallState.INCOMING);
    setupSessionHandlers(incomingSession);
    
    if (autoAnswer) {
      // Pick up immediately. Skip ringtone and notification.
      setTimeout(() => {
        try {
          const opts = {
            mediaConstraints: { audio: true, video: false },
            pcConfig: { iceServers: [] },
          };
          incomingSession.answer(opts);
          console.log('[CX] auto-answered queue call');
          // Engagement model (2026-07-27): report a NEW user leg going live.
          // The coordinator starts a recording if none is active. Uses the
          // Asterisk uniqueid the SSE stashed seconds earlier so the screen
          // rec links cleanly to the voice rec.
          (() => {
            const sr = window.__cxScreenRec;
            if (!sr) return;
            const callId = (
              window.__cxPendingScreenRecCallIdUntil
              && Date.now() < window.__cxPendingScreenRecCallIdUntil
            ) ? window.__cxPendingScreenRecCallId : null;
            // Consume the pending stash so it can't leak into the next call.
            window.__cxPendingScreenRecCallId = null;
            window.__cxPendingScreenRecCallIdUntil = 0;
            // Engagement model: if this auto-answer is the conf-join leg that
            // the backend Originated as part of a conference MERGE, it is a
            // CONTINUATION of the ongoing interaction (the agent's queue leg
            // was just torn down by the merge). Treat it as continuation so
            // the recording keeps rolling through the merge instead of
            // closing + restarting. Otherwise it's a genuine new queue call.
            const isMergeJoin = conferenceMergedRef.current === true;
            const origin = isMergeJoin ? 'continuation' : 'user';
            console.log('[CX-SR] inbound (auto) legLive, callId=', callId,
                        'origin=', origin, '(mergeJoin=', isMergeJoin, ')');
            sr.legLive(incomingSession, {
              origin,
              callId,
              agentExtension: (lastCreds.current && lastCreds.current.extension) || null,
            });
          })();
        } catch (err) {
          console.error('[CX] auto-answer failed', err);
        }
      }, 100);
      return;
    }

    // Play ringtone
    playRingtone();
    
    // Desktop notification
    if (CONFIG.notifications.desktop && Notification.permission === 'granted') {
      new Notification('Incoming Call', {
        body: `From: ${remoteIdentity.display_name || remoteIdentity.uri.user}`,
        icon: '/favicon.ico',
        requireInteraction: true
      });
    }
  };
  
  const setupSessionHandlers = (currentSession) => {
    console.log('[CX] setupSessionHandlers, direction:', currentSession.direction);
    
    currentSession.on('sending', (e) => {
      console.log('[CX] session: sending', e.request && e.request.method);
    });
    currentSession.on('connecting', () => {
      console.log('[CX] session: connecting (peerconnection being established)');
    });
    currentSession.on('progress', (e) => {
      const status = e && e.response && e.response.status_code;
      console.log('[CX] session: progress', status);
      if (currentSession.direction === 'outgoing') {
        setCallState(CallState.RINGING);
        // Start local ringback tone on 180 Ringing (no early media from SBC).
        // If the response has SDP we'd be hearing the network ringback already,
        // but in our setup Kamailio relays a Content-Length:0 180 so we synth.
        const hasEarlyMedia =
          e && e.response && e.response.body && e.response.body.length > 0;
        if (!hasEarlyMedia && status === 180) {
          playOutgoingRingback();
        }
      }
    });
    
    currentSession.on('accepted', () => {
      console.log('[CX] session: accepted (200 OK)');
      // If we're in a merged conference state, the auto-answered Originate
      // from backend that just accepted is the agent's new conf-join leg.
      // Don't downgrade to CONNECTED - keep CONFERENCING so the Release
      // button stays visible.
      // 2026-08-03: ONLY stay CONFERENCING if we're inside the active
      // merge window (a genuine conf-join leg arrives ~1-2s after merge).
      // A stale conferenceMergedRef (leaked from an abnormally-ended prior
      // conference) must NOT force a fresh call into CONFERENCING -- that
      // hid the Hold button on a plain inbound call (61059 bug). If we find
      // the flag stuck true outside the window, clear it here as a safety net.
      if (conferenceMergedRef.current && _inMergeWindow()) {
        console.log('[CX] session accepted while merged (in window) - staying CONFERENCING');
        setCallState(CallState.CONFERENCING);
      } else {
        if (conferenceMergedRef.current && !_inMergeWindow()) {
          console.warn('[CX] stale conferenceMerged flag on accept -> clearing (not a conf-join)');
          conferenceMergedRef.current = false;
          setConferenceMerged(false);
          setConferenceRoom(null);
        }
        setCallState(CallState.CONNECTED);
      }
      stopRingtone();
      startDurationTimer();
      startQualityMonitoring();
      
      setCallInfo(prev => ({
        ...prev,
        startTime: Date.now()
      }));
    });
    
    currentSession.on('confirmed', () => {
      console.log('[CX] session: confirmed (ACK received)');
    });
    
    currentSession.on('ended', (e) => {
      console.log('[CX] session: ended', e && e.cause);

      // Multi-call bugfix (2026-07-09): if this ended session is currently
      // PARKED (i.e. lives in heldCalls, not in session.current), it means
      // a held call was hung up remotely (or terminated locally via the
      // chip's ✕). We must NOT run the normal cleanupCall() path — that
      // would nuke session.current, which right now points at a DIFFERENT
      // (still-active) call. Just silently remove the chip and bail.
      //
      // Symptoms this fixes: held call chip stuck in tray with timer
      // running after remote BYE, then "swap back" to the ghost eventually
      // dropping and taking timers with it. Repro: Call1 → hold → New Call
      // → Call2 → hold → resume Call1 → Call2 hangs up.
      {
        const heldMatch = heldCallsRef.current.find(h => h.session === currentSession);
        if (heldMatch) {
          console.log('[CX-MC] held call ended (BYE while parked), removing chip', heldMatch.id);
          setHeldCalls(prev => prev.filter(h => h.session !== currentSession));
          // Engagement model: this parked leg is now gone. Report it so the
          // coordinator decrements. If other legs remain, recording keeps
          // rolling; if this was the last leg, the wrap tail arms.
          (() => {
            const sr = window.__cxScreenRec;
            if (sr && sr.legGone) sr.legGone(currentSession);
          })();
          // Deliberately do NOT: touch session.current, call cleanupCall(),
          // or fire wrap-up (agent may still be on another call).
          return;
        }
      }

      stopRingtone();

      // Engagement model (2026-07-27): report this leg gone. The coordinator
      // stops the recording only when ALL legs are gone (+ 10s wrap tail).
      // This is what lets a single recording span holds / transfers /
      // conferences without stopping mid-interaction.
      (() => {
        const sr = window.__cxScreenRec;
        if (!sr) return;
        if (sr.legGone) sr.legGone(currentSession);
        else sr.stopRecording().catch(() => {});  // legacy fallback
      })();

      // Phase 4: when an inbound call ends, fire a window event so the
      // AgentStatusBar can start the wrap-up countdown. We only fire for
      // inbound queue calls — detected by the queue-call-completed flag we
      // armed via SSE on incoming, OR by the call direction being inbound.
      //
      // CRITICAL: suppress this during conference merge. The backend
      // hangs up session A (the queue call) as part of the merge flow,
      // so the session-ended fires - but the agent is NOT actually done
      // with the call (they're about to receive the auto-answered
      // conf-join call). Wrap should only fire once on releaseConference
      // (which dispatches its own cx-call-ended-inbound).
      try {
        // 2026-07-28 (bug #3): fire the wrap-up trigger for BOTH inbound and
        // outbound calls. Requirement: agent always goes wrap(15s)->ready
        // after ANY call (inbound, outbound, consult, conference, transfer).
        // The AgentStatusBar state guard (transientStates) still prevents
        // dragging a deliberately not-ready/logout agent to Ready, so it's
        // safe to dispatch for outbound too (a callback from a logged-out
        // agent won't be pulled into wrap).
        const dir = currentSession.direction;
        const wasCall = dir === 'incoming' || dir === 'outgoing';
        const isMergeTransition = conferenceMergedRef.current;
        console.log('[CX-WRAP-TRIGGER] session-ended decision:', {
          direction: dir,
          wasCall,
          isMergeTransition,
          conferenceMerged: conferenceMergedRef.current,
          cause: e && e.cause,
        });
        if (wasCall && !isMergeTransition) {
          console.log('[CX-WRAP-TRIGGER] dispatching cx-call-ended-inbound');
          window.dispatchEvent(new CustomEvent('cx-call-ended-inbound', {
            detail: { cause: e && e.cause, direction: dir, ts: Date.now() }
          }));
        } else if (isMergeTransition) {
          console.log('[CX-WRAP-TRIGGER] SUPPRESSED (merge in progress)');
        } else {
          console.log('[CX-WRAP-TRIGGER] SUPPRESSED (no call direction)');
        }
      } catch (err) {
        console.warn('[CX] could not dispatch call-ended event', err);
      }

      cleanupCall();
    });
    
    currentSession.on('failed', (e) => {
      // Multi-call bugfix (2026-07-09): same guard as 'ended' — if this
      // failed session is PARKED, just remove the chip. Don't clobber
      // session.current or surface a scary error banner for the active call.
      {
        const heldMatch = heldCallsRef.current.find(h => h.session === currentSession);
        if (heldMatch) {
          console.log('[CX-MC] held call failed while parked, removing chip', heldMatch.id, e && e.cause);
          setHeldCalls(prev => prev.filter(h => h.session !== currentSession));
          (() => {
            const sr = window.__cxScreenRec;
            if (sr && sr.legGone) sr.legGone(currentSession);
          })();
          return;
        }
      }

      // Engagement model (2026-07-27): report this leg gone on failed too
      // (cancel, reject, network error). Coordinator arms the wrap tail if
      // this was the last leg.
      (() => {
        const sr = window.__cxScreenRec;
        if (!sr) return;
        if (sr.legGone) sr.legGone(currentSession);
        else sr.stopRecording().catch(() => {});  // legacy fallback
      })();
      // ---- original handler below ----
      console.error('[CX] session: FAILED ->', e.cause, e);
      stopRingtone();
      setError(`Call failed: ${e.cause}`);
      cleanupCall();
    });
    
    currentSession.on('getusermediafailed', (err) => {
      console.error('[CX] session: getUserMedia FAILED ->', err);
      setError(`Microphone access denied or unavailable`);
    });
    
    currentSession.on('hold', (e) => {
      if (e.originator === 'local') {
        setIsLocalHold(true);
        setCallState(CallState.HOLDING);
      } else {
        setIsRemoteHold(true);
        setCallState(CallState.HELD);
      }
    });
    
    currentSession.on('unhold', (e) => {
      if (e.originator === 'local') {
        setIsLocalHold(false);
      } else {
        setIsRemoteHold(false);
      }
      if (!isLocalHold && !isRemoteHold) {
        setCallState(CallState.CONNECTED);
      }
    });
    
    currentSession.on('muted', (e) => {
      if (e.audio) setIsMuted(true);
    });
    
    currentSession.on('unmuted', (e) => {
      if (e.audio) setIsMuted(false);
    });
    
    // Handle remote audio - wire ontrack as soon as the peerconnection exists.
    // We use both 'peerconnection' (JsSIP event, fires when PC is constructed)
    // and 'addstream' (legacy event some versions still fire) as belt-and-suspenders.
    const wireRemoteAudio = (pc) => {
      if (!pc) return;
      // Track callback - fires when remote audio track arrives
      pc.ontrack = (event) => {
        console.log('[CX] ontrack fired, streams:', event.streams && event.streams.length);
        try {
          if (!remoteAudio.current) {
            remoteAudio.current = new Audio();
            remoteAudio.current.autoplay = true;
            // Some browsers need an explicit play() call after setting srcObject
            remoteAudio.current.muted = false;
            // Add to DOM so devtools can find it for debugging
            remoteAudio.current.id = 'cx-remote-audio';
            try { document.body.appendChild(remoteAudio.current); } catch (e) {}
          }
          const stream = event.streams && event.streams[0];
          if (stream) {
            remoteAudio.current.srcObject = stream;
            console.log('[CX] ontrack: audio tracks in stream =', stream.getAudioTracks().length);
          } else if (event.track) {
            // Fallback: assemble a stream from the single track
            const ms = new MediaStream([event.track]);
            remoteAudio.current.srcObject = ms;
            console.log('[CX] ontrack fallback: built stream from single track');
          }
          // Force play (some browsers won't autoplay despite the flag)
          const p = remoteAudio.current.play();
          if (p && p.catch) {
            p.catch((err) => {
              console.warn('[CX] remoteAudio.play() failed, click anywhere to enable:', err.message);
            });
          }
          if (selectedOutput && remoteAudio.current.setSinkId) {
            remoteAudio.current.setSinkId(selectedOutput).catch((e) =>
              console.warn('[CX] setSinkId failed:', e.message));
          }
        } catch (err) {
          console.error('[CX] ontrack handler failed:', err);
        }
      };
    };

    currentSession.on('peerconnection', (e) => {
      console.log('[CX] peerconnection event received in setupSessionHandlers');
      wireRemoteAudio(e.peerconnection);
    });

    // If peerconnection was already created BEFORE we attached the listener
    // (race condition - JsSIP creates it synchronously in ua.call()),
    // wire it now from the session.connection reference.
    if (currentSession.connection) {
      console.log('[CX] peerconnection already exists, wiring directly');
      wireRemoteAudio(currentSession.connection);
    }
  };
  
  const cleanupCall = () => {
    stopDurationTimer();
    stopQualityMonitoring();
    stopRingtone();

    // 2026-07-23: only teardown the local mic stream if we have NO held
    // calls. If a held call still exists (multi-call scenario — e.g. the
    // agent hung up an outbound consult and the original inbound is still
    // parked in the held-tray), the mic track is shared by the held
    // session's peer connection. Calling track.stop() puts the track in a
    // terminal state that can't be recovered, permanently killing the
    // agent's mic on the held session. Symptom: after return from consult,
    // caller can't hear agent.
    if (localStream.current) {
      const hasHeldCalls = heldCallsRef.current.length > 0;
      if (!hasHeldCalls) {
        localStream.current.getTracks().forEach(t => t.stop());
        localStream.current = null;
      } else {
        console.log('[CX] cleanupCall: preserving localStream (held calls exist)',
                    heldCallsRef.current.length);
      }
    }

    session.current = null;
    currentSessionIdRef.current = null;
    // 2026-07-23: preserve callState if we have held calls, so the UI can
    // show the held-tray as the primary state instead of dropping to IDLE.
    if (heldCallsRef.current.length === 0) {
      setCallState(CallState.IDLE);
    } else {
      setCallState(CallState.IDLE);  // still IDLE because no active call, but held tray remains visible
    }
    setCallInfo(null);
    setCallDuration(0);
    setIsMuted(false);
    setIsLocalHold(false);
    setIsRemoteHold(false);
    setTransferState(null);
    setConsultSession(null);
    setCallQuality(null);
    // 2026-08-03: clear conference state on genuine call teardown so it can
    // NEVER leak into the next call. GUARD: during a merge transition the
    // backend ends session A while a conf-join leg is arriving -- in that
    // narrow window we must PRESERVE the conference flags. Outside the window
    // (i.e. the conference truly ended, or this was never a conference), we
    // reset. This is the fix for a plain inbound call showing the conference
    // panel with no Hold button (61059, 2026-08-03).
    if (!_inMergeWindow()) {
      if (conferenceMergedRef.current) {
        console.log('[CX] cleanupCall: clearing stale conference state (outside merge window)');
      }
      conferenceMergedRef.current = false;
      setConferenceMerged(false);
      setConferenceRoom(null);
    }
  };

  // -------------------------------------------------------------------
  // Phantom-session detector (safety net, 2026-07-02)
  //
  // Every 5 seconds, if we THINK there's an active call but the underlying
  // RTCPeerConnection is in a terminal state ('closed' or 'failed') or the
  // JsSIP session has ended without firing on('ended'), force a cleanup.
  // Guards against BYE-not-received / handler-not-fired bugs.
  // -------------------------------------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      const s = session.current;
      if (!s) return; // Nothing to check.
      if (callState === CallState.IDLE) return; // UI already knows.
      let pcState = null;
      let sessionEnded = false;
      try {
        if (typeof s.isEnded === 'function' && s.isEnded()) sessionEnded = true;
        // JsSIP internal state: session._is_canceled or similar might also help,
        // but isEnded() is the canonical.
        const pc = s.connection;
        pcState = pc && pc.connectionState;
      } catch (_) { /* ignore introspection errors */ }
      const iceState = (() => {
        try { return s.connection && s.connection.iceConnectionState; } catch (_) { return null; }
      })();
      const terminal = (
        sessionEnded
        || pcState === 'closed'
        || pcState === 'failed'
        || iceState === 'closed'
        || iceState === 'failed'
      );
      if (terminal) {
        console.warn('[CX] phantom-session detected — cleaning up', {
          callState, pcState, iceState, sessionEnded,
        });
        try { s.terminate?.(); } catch (_) { /* fine */ }
        cleanupCall();
      }
    }, 5000);
    return () => clearInterval(interval);
    // Rebind when callState changes so the closure has fresh values.
  }, [callState]);
  
  // ═══════════════════════════════════════════════════════════════
  // CALL CONTROLS
  // ═══════════════════════════════════════════════════════════════
  
  const call = useCallback(async (target) => {
    console.log('[CX] call() invoked for target:', target);
    
    // ----- Pre-flight checks -----
    if (!ua.current) {
      const msg = 'Cannot call: SIP UA not initialised';
      console.error('[CX]', msg);
      setError(msg);
      return;
    }
    if (connectionState !== ConnectionState.REGISTERED) {
      const msg = `Cannot call: not registered (state=${connectionState})`;
      console.error('[CX]', msg);
      setError(msg);
      return;
    }
    if (!currentServer || !currentServer.sipDomain) {
      const msg = 'Cannot call: currentServer missing sipDomain';
      console.error('[CX]', msg, currentServer);
      setError(msg);
      return;
    }
    if (!target || !String(target).trim()) {
      const msg = 'Cannot call: empty target';
      console.error('[CX]', msg);
      setError(msg);
      return;
    }
    // MC-1: if there's already an active call, park it first so we can
    // start a fresh one. If we're at the hard cap, refuse.
    if (session.current && callState !== CallState.IDLE) {
      if (heldCallsRef.current.length + 1 >= MAX_CONCURRENT_CALLS) {
        const msg = `Cannot start a new call: at max of ${MAX_CONCURRENT_CALLS} concurrent calls`;
        console.warn('[CX-MC]', msg);
        setError(msg);
        return;
      }
      console.log('[CX-MC] auto-parking active call to place new outbound');
      parkCurrent();
      // Give React a beat to flush the state before we continue — the
      // downstream JsSIP call() runs synchronously and expects
      // session.current === null.
      await new Promise((r) => setTimeout(r, 50));
    }

    // ----- Outbound dialing profile pre-check (UX-only, dialplan is the
    //       authoritative enforcer). Adds ~30-80 ms of latency before
    //       INVITE; in exchange the agent gets an instant clean error
    //       message instead of "all circuits busy" treatment.
    // ----------------------------------------------------------------
    try {
      const decision = await softphoneApi.dialPrecheck(String(target).trim());
      console.log('[CX] dialPrecheck:', decision);
      if (decision && decision.allowed === false) {
        const reason = decision.reason || 'denied';
        const profile = decision.profile_name || 'Default Block';
        const dest = decision.destination_e164 || target;
        let userMsg;
        switch (reason) {
          case 'invalid_format':
            userMsg = `"${target}" is not a valid phone number format.`;
            break;
          case 'no_profile_default_block':
            userMsg = `No outbound dialing profile is assigned to your account. Ask an admin to enable dialing.`;
            break;
          case 'country_blocked':
            userMsg = `${dest} is blocked by your dialing profile (${profile}).`;
            break;
          case 'domestic_us_not_allowed':
            userMsg = `Domestic US dialing is not allowed by your profile (${profile}).`;
            break;
          case 'canada_not_allowed':
            userMsg = `Canada dialing is not allowed by your profile (${profile}).`;
            break;
          case 'mexico_not_allowed':
            userMsg = `Mexico dialing is not allowed by your profile (${profile}).`;
            break;
          case 'intl_not_allowed':
            userMsg = `International dialing is not allowed by your profile (${profile}).`;
            break;
          default:
            userMsg = `Outbound call denied: ${reason}.`;
        }
        setError(userMsg);
        // Stay in IDLE so the dialer UI doesn't move to CONNECTING.
        return;
      }
    } catch (precheckErr) {
      // Pre-check failed (network blip etc.) - allow the call. The dialplan
      // still enforces. We log + carry on rather than block the user.
      console.warn('[CX] dialPrecheck failed (allowing call to proceed):', precheckErr);
    }
    
    // NOTE: We used to probe getUserMedia here, but releasing the probe stream
    // sometimes leaves the mic in a half-released state on Chrome, causing
    // JsSIP's own getUserMedia call to hang and never emit the INVITE.
    // We now let JsSIP own the mic acquisition end-to-end.
    
    // ----- Move to CONNECTING state so UI shows Cancel button -----
    setError(null);
    setCallState(CallState.CONNECTING);
    setCallInfo({
      direction: 'outbound',
      remoteNumber: target,
      remoteName: null,
      startTime: null
    });
    
    const targetUri = `sip:${target}@${currentServer.sipDomain}`;
    console.log('[CX] Placing JsSIP call to:', targetUri);
    
    // Build pcConfig. If STUN is blocked (corp/home FW often drops UDP/19302),
    // ICE gathering can hang waiting on the STUN response. We do two things:
    //   1. Provide STUN servers but with a hard gathering timeout via JsSIP option
    //   2. Disable trickle-blocking by limiting iceCheckingTimeout in JsSIP
    const pcConfig = {
      iceServers: CONFIG.ice.stunServers && CONFIG.ice.stunServers.length
        ? CONFIG.ice.stunServers.map(url => ({ urls: url }))
        : [],
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require'
    };
    console.log('[CX] pcConfig.iceServers:', pcConfig.iceServers);
    
    const options = {
      mediaConstraints: {
        audio: selectedInput 
          ? { deviceId: { exact: selectedInput } }
          : true,
        video: false
      },
      pcConfig,
      // Don't wait forever for ICE gathering to complete before sending INVITE.
      // 2000ms is plenty for host + reflexive candidates on most networks.
      // After this, JsSIP sends the offer with whatever it has and trickles
      // additional candidates as they arrive.
      rtcOfferConstraints: {
        offerToReceiveAudio: 1,
        offerToReceiveVideo: 0
      }
    };
    
    // ----- Place the call inside try/catch so any JsSIP exception surfaces -----
    try {
      session.current = ua.current.call(targetUri, options);
      currentSessionIdRef.current = `call-${Date.now()}`;
      console.log('[CX] JsSIP.call() returned session:', !!session.current);
      
      if (!session.current) {
        const msg = 'JsSIP.call() returned no session (call rejected by UA)';
        console.error('[CX]', msg);
        setError(msg);
        setCallState(CallState.IDLE);
        return;
      }
      
      setupSessionHandlers(session.current);

      // Phase 2.2 v2 (2026-07-02): per-call screen recording for OUTBOUND
      // calls. Start IMMEDIATELY with the SIP Call-ID as placeholder —
      // recording begins uploading right away, no dead air on screen.
      // Backend AMI subscriber emits `outbound-call-started` SSE with the
      // Asterisk uniqueid within a few hundred ms; the SSE handler above
      // then rebinds the recording's call_id to that uniqueid, so voice↔
      // screen linking Just Works.
      (() => {
        const sr = window.__cxScreenRec;
        if (!sr) return;
        // Check if the SSE already arrived before ua.call() completed
        // (rare but possible if Asterisk was very fast).
        const now = Date.now();
        const preArrived = (
          window.__cxPendingOutboundCallIdUntil
          && now < window.__cxPendingOutboundCallIdUntil
        ) ? window.__cxPendingOutboundCallId : null;
        // Consume the pre-arrived stash if any.
        if (preArrived) {
          window.__cxPendingOutboundCallId = null;
          window.__cxPendingOutboundCallIdUntil = 0;
        }
        const jsSipCallId = (() => {
          try {
            const req = session.current && (session.current._request || session.current.request);
            return req && req.call_id ? req.call_id : null;
          } catch (_) { return null; }
        })();
        const initialCallId = preArrived || jsSipCallId;
        console.log(
          '[CX-SR] outbound legLive, initial callId=', initialCallId,
          '(preArrived=', preArrived, ', jsSipCallId=', jsSipCallId, ')',
        );
        // Engagement model (2026-07-27): outbound dial is a NEW user leg.
        // 2026-08-17 FIX 1: this browser start and the server `screen-record-start`
        // SSE now COOPERATE instead of racing -- both funnel through the engagement
        // coordinator (this via legLive, the SSE via serverStart which DEBOUNCES
        // against an active/'preparing' engagement + rebinds the uniqueid). So
        // whichever fires first starts the single recording; the other no longer
        // pre-empts it. Keeping this dial-time start avoids dead-air-on-screen.
        sr.legLive(session.current, {
          origin: 'user',
          callId: initialCallId,
          agentExtension: (lastCreds.current && lastCreds.current.extension) || null,
        });
      })();
      
      // Promote state to RINGING right away so user sees the Cancel button
      // even if JsSIP's progress event is slow
      setCallState(CallState.RINGING);
      
      // Watch the peerconnection: store local stream AND force-end ICE
      // gathering after a short timeout so the INVITE goes out even if
      // STUN is blocked.
      session.current.on('peerconnection', (e) => {
        const pc = e.peerconnection;
        console.log('[CX] peerconnection created');
        
        try {
          const streams = pc.getLocalStreams ? pc.getLocalStreams() : [];
          localStream.current = streams[0] || null;
        } catch (innerErr) {
          console.warn('[CX] Could not capture local stream:', innerErr);
        }
        
        // Log ICE gathering state changes for debugging
        pc.addEventListener('icegatheringstatechange', () => {
          console.log('[CX] ICE gathering state:', pc.iceGatheringState);
        });
        pc.addEventListener('icecandidate', (ev) => {
          if (ev.candidate) {
            console.log('[CX] ICE candidate:', ev.candidate.candidate);
          } else {
            console.log('[CX] ICE gathering complete (null candidate)');
          }
        });
        pc.addEventListener('iceconnectionstatechange', () => {
          console.log('[CX] ICE connection state:', pc.iceConnectionState);
        });

        // Wrap setRemoteDescription so we can see the EXACT SDP that
        // fails to parse, plus the underlying WebRTC error.
        const origSetRemote = pc.setRemoteDescription.bind(pc);
        pc.setRemoteDescription = function (desc) {
          console.log('[CX] setRemoteDescription type=' + (desc && desc.type) +
            ' sdp:\n' + (desc && desc.sdp));
          return origSetRemote(desc).catch((err) => {
            console.error('[CX] setRemoteDescription FAILED:', err && err.message, '\n' +
              'SDP body that failed:\n' + (desc && desc.sdp));
            throw err;
          });
        };
        const origSetLocal = pc.setLocalDescription.bind(pc);
        pc.setLocalDescription = function (desc) {
          console.log('[CX] setLocalDescription type=' + (desc && desc.type) +
            ' sdp:\n' + (desc && desc.sdp));
          return origSetLocal(desc).catch((err) => {
            console.error('[CX] setLocalDescription FAILED:', err && err.message, '\n' +
              'SDP body that failed:\n' + (desc && desc.sdp));
            throw err;
          });
        };
        
        // SAFETY NET: if ICE gathering hasn't completed after 2 seconds,
        // force-finish by dispatching a null candidate event. JsSIP listens
        // for that to decide when the offer is ready to send.
        setTimeout(() => {
          if (pc.iceGatheringState !== 'complete') {
            console.warn('[CX] ICE gathering still in state', pc.iceGatheringState, 'after 2s - this may indicate STUN is blocked');
          }
        }, 2000);
      });
    } catch (err) {
      const msg = `JsSIP.call() threw: ${err.message || err}`;
      console.error('[CX]', msg, err);
      setError(msg);
      setCallState(CallState.IDLE);
      session.current = null;
    }
  }, [connectionState, currentServer, selectedInput]);
  
  const answer = useCallback(() => {
    if (!session.current) return;
    
    const options = {
      mediaConstraints: {
        audio: selectedInput 
          ? { deviceId: { exact: selectedInput } }
          : true,
        video: false
      }
    };
    
    const answeredSession = session.current;
    answeredSession.answer(options);
    stopRingtone();

    // Engagement model (2026-07-27): manual answer was previously MISSING a
    // screen-recording trigger entirely (root cause of inconsistent QA
    // coverage). Report the leg live so the coordinator starts recording.
    (() => {
      const sr = window.__cxScreenRec;
      if (!sr) return;
      const callId = (
        window.__cxPendingScreenRecCallIdUntil
        && Date.now() < window.__cxPendingScreenRecCallIdUntil
      ) ? window.__cxPendingScreenRecCallId : null;
      window.__cxPendingScreenRecCallId = null;
      window.__cxPendingScreenRecCallIdUntil = 0;
      console.log('[CX-SR] inbound (manual) legLive, callId=', callId);
      sr.legLive(answeredSession, {
        origin: 'user',
        callId,
        agentExtension: (lastCreds.current && lastCreds.current.extension) || null,
      });
    })();
  }, [selectedInput]);
  
  const hangup = useCallback(() => {
    if (session.current) {
      session.current.terminate();
    }
    cleanupCall();
  }, []);
  
  // 2026-07-23: server-side hold via AMI. Bypasses SIP re-INVITE entirely
  // to avoid rtpengine ICE regeneration (which broke audio in our topology
  // — see the day's investigation logs). The peer connection stays in
  // sendrecv throughout the call; hold UX is delivered by:
  //   (a) local track enable/disable on the browser side
  //   (b) AMI MusicOnHold on the caller's SIP channel server-side
  //
  // Reporting stays intact: Asterisk fires MusicOnHoldStart/Stop AMI
  // events which ami_subscriber already tracks (active_call_hold +
  // calls_hold_events). We also write audit_events rows for a clean
  // audit trail (see routers/softphone.py hold_start/stop).
  //
  // Transfer + conference + multi-call held-tray paths still use the
  // real SIP session.hold() because they need actual SDP direction
  // changes to enable consult media. Only the plain Hold button changes.
  const hold = useCallback(async () => {
    console.log('[CX] hold: invoked (server-side MOH)', {
      hasSession: !!session.current,
      callState,
    });
    if (!session.current || callState !== CallState.CONNECTED) {
      console.warn('[CX] hold: GUARD REJECTED');
      return;
    }
    const s = session.current;

    // Step 1: DEAFEN agent locally (disable receiver tracks) so they
    // don't hear the caller. But KEEP THE SENDER TRACK ENABLED so
    // Asterisk's chan_sip keeps seeing incoming RTP from the browser —
    // otherwise the 31s RTP timeout kills the agent's SIP channel while
    // it's parked in cx-hold-agent-wait. The agent's mic audio arriving
    // at Asterisk during hold is harmless: the agent channel is in
    // MusicOnHold() with no bridge, so incoming RTP is dropped.
    //
    // The caller ALSO doesn't hear the agent because the caller channel
    // is in a separate context (cx-hold-loop) and there's no bridge
    // between them until Unhold fires.
    try {
      const pc = s.connection;
      if (pc?.getReceivers) {
        for (const r of pc.getReceivers()) {
          if (r.track) r.track.enabled = false;
        }
      }
      // Sender track intentionally left enabled. See comment above.
    } catch (err) {
      console.warn('[CX] hold: local track disable threw', err);
    }

    // Step 2: reflect hold state in UI immediately.
    setIsLocalHold(true);
    setCallState(CallState.HOLDING);

    // Step 3: backend AMI MOH on the caller's SIP channel.
    // 2026-08-03 FIX: previously a server-hold failure was treated as
    // "not fatal" and we LEFT the agent deafened (step 1) + UI in hold.
    // But if the server never redirected the caller into cx-hold-loop,
    // the caller is STILL BRIDGED to the agent and hears the agent's mic
    // (sender track stays enabled) while the agent hears nothing = the
    // reported 61049 half-state ("caller hears agent, no MOH, agent deaf").
    // Correct behavior: if the server hold fails, ROLL BACK the local
    // deafen + hold UI so the call stays fully two-way connected (NOT on
    // hold) rather than trapping the agent in a broken silent state.
    let serverHoldOk = false;
    try {
      const resp = await softphoneApi.holdStart();
      console.log('[CX] hold: backend MOH result', resp);
      serverHoldOk = !!(resp && resp.ok);
      if (!serverHoldOk) {
        console.warn('[CX] hold: backend MOH failed', resp && resp.reason);
      }
    } catch (err) {
      console.warn('[CX] hold: backend MOH request threw', err);
    }
    if (!serverHoldOk) {
      // Roll back: re-enable receiver tracks + revert UI to CONNECTED so
      // audio is restored both ways and the agent is not stuck deaf.
      try {
        const pc = s.connection;
        if (pc?.getReceivers) {
          for (const r of pc.getReceivers()) {
            if (r.track) r.track.enabled = true;
          }
        }
      } catch (err) {
        console.warn('[CX] hold: rollback track re-enable threw', err);
      }
      try { _reattachRemoteAudio(s); } catch (_) {}
      setIsLocalHold(false);
      setCallState(CallState.CONNECTED);
      console.warn('[CX] hold: server hold failed -> rolled back to CONNECTED (call NOT on hold)');
    }
  }, [callState]);

  // 2026-07-23 fix: single-call unhold used to just call session.unhold()
  // and rely on JsSIP + Asterisk to sort the audio out. Users reported
  // (Jordany Vasquez 61002, inbound from +13476613198 at 09:48 MDT) that
  // after Hold + Unhold, the customer couldn't hear the agent anymore.
  // Asterisk log confirmed MOH started/stopped on the Local channel leg
  // when Hold pressed, but audio never recovered on either direction
  // after Unhold.
  //
  // Root cause: JsSIP's session.hold() disables the local audio sender's
  // track and flips the peer-connection direction to sendonly. On unhold
  // it re-INVITEs with sendrecv, but on some Chrome/rtpengine paths the
  // MediaStream in the remote <audio> element and the sender track's
  // `enabled` flag don't fully recover without explicit intervention.
  //
  // Multi-call `resumeHeld` and attended-transfer end-of-consult already
  // paper over this by calling `_reattachRemoteAudio` + re-enabling
  // receiver tracks. Plain unhold didn't. This aligns the plain path
  // with the working multi-call path:
  //   1. session.unhold() sends the re-INVITE (sendrecv).
  //   2. Re-enable receiver tracks in case JsSIP left them disabled.
  //   3. Re-enable sender tracks so the agent's mic reaches the customer.
  //   4. _reattachRemoteAudio() polls for live tracks post-re-INVITE
  //      and swaps them into the remote <audio> element.
  const unhold = useCallback(async () => {
    console.log('[CX] unhold: invoked (server-side StopMOH)', {
      hasSession: !!session.current,
      callState,
    });
    if (!session.current || callState !== CallState.HOLDING) {
      console.warn('[CX] unhold: GUARD REJECTED');
      return;
    }
    const s = session.current;

    // 2026-07-23: server-side hold path. No SIP re-INVITE. Ask the
    // backend to stop MOH, then re-enable local tracks. The peer
    // connection was never re-INVITEd so tracks just resume flowing.
    try {
      const resp = await softphoneApi.holdStop();
      console.log('[CX] unhold: backend StopMOH result', resp);
    } catch (err) {
      console.warn('[CX] unhold: backend request threw', err);
    }
    try {
      const pc = s.connection;
      if (pc?.getSenders) {
        for (const snd of pc.getSenders()) {
          if (snd.track) snd.track.enabled = true;
        }
      }
      if (pc?.getReceivers) {
        for (const r of pc.getReceivers()) {
          if (r.track) r.track.enabled = true;
        }
      }
    } catch (err) {
      console.warn('[CX] unhold: track re-enable threw', err);
    }
    // Rewire the remote audio element in case Asterisk's Bridge()
    // switched the RTP source and Chrome fired a fresh ontrack.
    _reattachRemoteAudio(s);
    setIsLocalHold(false);
    setCallState(CallState.CONNECTED);
    return;

    // ==== LEGACY SIP-HOLD PATH (dead code below, kept for reference) ====
    // The return above short-circuits everything below. If we ever need
    // to revert to SIP-hold, remove the return and delete this comment.
    // eslint-disable-next-line no-unreachable
    try { s.unhold(); } catch (err) {
      console.warn('[CX] unhold: session.unhold() threw', err);
    }
    // Re-enable both receiver and sender tracks defensively. JsSIP flips
    // sender.track.enabled during hold on some code paths; nothing else
    // will turn it back on if the peer-connection direction alone doesn't
    // trigger a track-state change.
    // 2026-07-23 11:00 pass 2: agent → customer audio still broken after
    // pass 1 (Fernando ext 60009 test). Additional fix: force each audio
    // transceiver's direction back to 'sendrecv' explicitly. Chrome caches
    // transceiver.direction independently from what SDP re-negotiation
    // says, so if hold left it 'sendonly' the outbound RTP stays gated
    // even after unhold's sendrecv SDP is answered.
    try {
      const pc = s.connection;
      if (pc?.getReceivers) {
        for (const r of pc.getReceivers()) {
          if (r.track) r.track.enabled = true;
        }
      }
      if (pc?.getSenders) {
        for (const snd of pc.getSenders()) {
          if (snd.track) snd.track.enabled = true;
        }
      }
      // Force each audio transceiver to sendrecv. This is the direction
      // agents want during a normal bridged call. If a transceiver is
      // in a weird state ('sendonly', 'recvonly', 'inactive') Chrome will
      // gate outbound RTP even when the track is enabled and the sender
      // has a live source. `direction=sendrecv` tells Chrome to actually
      // pump the encoded audio out.
      if (pc?.getTransceivers) {
        for (const t of pc.getTransceivers()) {
          try {
            const kind =
              (t.sender && t.sender.track && t.sender.track.kind) ||
              (t.receiver && t.receiver.track && t.receiver.track.kind) ||
              'audio';
            console.log('[CX] unhold: transceiver inspect',
                        { mid: t.mid || '(no-mid)', kind,
                          direction: t.direction,
                          currentDirection: t.currentDirection,
                          senderTrackEnabled: t.sender?.track?.enabled,
                          senderTrackMuted: t.sender?.track?.muted,
                          receiverTrackEnabled: t.receiver?.track?.enabled,
                          receiverTrackMuted: t.receiver?.track?.muted,
                        });
            if (kind === 'audio' && t.direction !== 'sendrecv') {
              console.log('[CX] unhold: forcing transceiver',
                          t.mid || '(no-mid)', 'from', t.direction, '→ sendrecv');
              t.direction = 'sendrecv';
            }
          } catch (err) {
            console.warn('[CX] unhold: transceiver inspect threw', err);
          }
        }
      }
    } catch (err) {
      console.warn('[CX] unhold: track re-enable threw', err);
    }
    // Rewire the remote audio element. Polls until a live track appears
    // (post-re-INVITE renegotiation), up to 3 s.
    _reattachRemoteAudio(s);

    // Delayed diagnostic: log the FINAL state 3 s after unhold so we can
    // see what Chrome actually settled on after the re-INVITE answer.
    setTimeout(() => {
      try {
        const pc = s.connection;
        if (!pc || !pc.getTransceivers) return;
        for (const t of pc.getTransceivers()) {
          const kind =
            (t.sender && t.sender.track && t.sender.track.kind) ||
            (t.receiver && t.receiver.track && t.receiver.track.kind) ||
            'unknown';
          console.log('[CX] unhold+3s: transceiver FINAL', {
            mid: t.mid, kind,
            direction: t.direction,
            currentDirection: t.currentDirection,
            senderTrackEnabled: t.sender?.track?.enabled,
            senderTrackMuted: t.sender?.track?.muted,
            senderTrackReadyState: t.sender?.track?.readyState,
            receiverTrackEnabled: t.receiver?.track?.enabled,
            receiverTrackMuted: t.receiver?.track?.muted,
            receiverTrackReadyState: t.receiver?.track?.readyState,
          });
        }
      } catch (err) {
        console.warn('[CX] unhold+3s: threw', err);
      }

      // Also grab an RTCStats snapshot so we can see if RTP is flowing.
      try {
        s.connection?.getStats?.().then(stats => {
          const summary = {};
          stats.forEach(r => {
            if (r.type === 'outbound-rtp' && r.kind === 'audio') {
              summary.outbound_rtp = {
                packetsSent: r.packetsSent,
                bytesSent: r.bytesSent,
                ssrc: r.ssrc,
              };
            }
            if (r.type === 'inbound-rtp' && r.kind === 'audio') {
              summary.inbound_rtp = {
                packetsReceived: r.packetsReceived,
                bytesReceived: r.bytesReceived,
                packetsLost: r.packetsLost,
                ssrc: r.ssrc,
              };
            }
          });
          console.log('[CX] unhold+3s: RTP stats', summary);
        });
      } catch (err) {
        console.warn('[CX] unhold+3s: getStats threw', err);
      }
    }, 3000);
  }, [callState]);

  // ---------------------------------------------------------------------
  // Multi-call MC-1 (2026-07-02): held-tray helpers.
  //
  // These do NOT touch the transfer / conference flows — those keep using
  // `session.current` + `consultSession` directly. The held tray is a
  // separate axis: a way to "park" an entire call so the agent can
  // start / accept another one, then come back.
  // ---------------------------------------------------------------------

  // Force the SIP session onto re-INVITE hold. `session.hold()` in JsSIP
  // returns immediately but the peer hasn't ACKed yet; we don't need to
  // wait — the media is already sendonly on our side after the local
  // offer, and Asterisk starts MOH within milliseconds.
  const _safeHold = (s) => { try { s?.hold?.(); } catch (_) {} };
  const _safeUnhold = (s) => { try { s?.unhold?.(); } catch (_) {} };

  // Re-attach remote audio to the active session's peerconnection.
  // Called after resume() promotes a held session back to active.
  // Wire the given session's remote audio into the shared <audio> element.
  //
  // 2026-07-15 pm fix: previously we grabbed the receiver tracks once,
  // 100 ms after unhold. On the first swap-back after a NewCall + consult
  // that was too early: the SDP re-INVITE for unhold hadn't finished
  // negotiating, receivers had a track but readyState="muted" or the
  // track wasn't yet flowing, so MediaStream played silence. Swapping
  // away and back happened to hit it after the re-INVITE landed.
  //
  // New behavior:
  //  * Attempt immediately.
  //  * Register a track handler on the peerconnection so any FUTURE
  //    track that arrives (from ontrack) gets attached too.
  //  * If we see no live-ready tracks, poll every 250 ms for up to 3 s.
  //  * When a live track shows up, wrap it in a fresh MediaStream and
  //    swap it into the shared <audio>.
  const _reattachRemoteAudio = (s) => {
    try {
      const pc = s && s.connection;
      if (!pc) return;

      const ensureAudioEl = () => {
        if (!remoteAudio.current) {
          remoteAudio.current = new Audio();
          remoteAudio.current.autoplay = true;
          remoteAudio.current.id = 'cx-remote-audio';
          try { document.body.appendChild(remoteAudio.current); } catch (_) {}
        }
        return remoteAudio.current;
      };

      const collectLiveTracks = () => {
        const receivers = pc.getReceivers ? pc.getReceivers() : [];
        return receivers
          .map(r => r.track)
          .filter(t => t && t.readyState === 'live' && !t.muted);
      };

      const attach = () => {
        const tracks = collectLiveTracks();
        if (!tracks.length) return false;
        const ms = new MediaStream(tracks);
        const el = ensureAudioEl();
        el.srcObject = ms;
        const p = el.play();
        if (p && p.catch) p.catch(() => {});
        if (selectedOutput && el.setSinkId) {
          el.setSinkId(selectedOutput).catch(() => {});
        }
        return true;
      };

      // Immediate attempt.
      if (attach()) return;

      // Poll for a live track for up to ~3s.
      let attempts = 0;
      const iv = setInterval(() => {
        attempts += 1;
        if (attach() || attempts >= 12) {
          clearInterval(iv);
        }
      }, 250);

      // Also register a one-shot listener so if a fresh track arrives
      // via ontrack after we start polling, we grab it right away.
      try {
        const onTrack = () => {
          if (attach()) {
            try { pc.removeEventListener('track', onTrack); } catch (_) {}
          }
        };
        pc.addEventListener('track', onTrack, { once: false });
        // Detach the listener after 5s regardless.
        setTimeout(() => {
          try { pc.removeEventListener('track', onTrack); } catch (_) {}
        }, 5000);
      } catch (_) {
        // pc may not support addEventListener in some JsSIP versions; the
        // poll fallback still runs.
      }

      // Also react to track unmute events (Chrome fires 'unmute' on the
      // MediaStreamTrack when the RTP starts flowing again after unhold).
      try {
        const receivers = pc.getReceivers ? pc.getReceivers() : [];
        for (const r of receivers) {
          if (!r.track) continue;
          const t = r.track;
          const onUnmute = () => {
            attach();
            try { t.removeEventListener('unmute', onUnmute); } catch (_) {}
          };
          try { t.addEventListener('unmute', onUnmute, { once: true }); } catch (_) {}
        }
      } catch (_) { /* swallow */ }
    } catch (err) {
      console.warn('[CX-MC] reattach remote audio failed', err);
    }
  };

  // parkCurrent(): move session.current → heldCalls and clear the primary
  // slot so a new call can start. Returns { id } or null.
  //
  // 2026-07-23: for inbound queue calls we use server-side hold (backend
  // AMI Redirect → cx-hold-loop + cx-hold-agent-wait) because SIP hold
  // triggers rtpengine ICE regeneration → broken audio on resume. For
  // outbound calls we still use SIP hold (JsSIP session.hold()) because
  // outbound is a direct SIP bridge with no Local-channel topology and
  // SIP hold works fine there.
  const parkCurrent = useCallback(() => {
    if (!session.current) return null;
    if (callState !== CallState.CONNECTED && callState !== CallState.HOLDING) {
      console.warn('[CX-MC] parkCurrent skipped, callState=', callState);
      return null;
    }
    const parkedSession = session.current;
    const parkedInfo = callInfoRef.current;
    const parkedId = currentSessionIdRef.current || `call-${Date.now()}`;
    const isInbound = (parkedInfo?.direction || 'unknown') === 'incoming';
    const alreadyOnServerHold = callState === CallState.HOLDING && isLocalHold;

    if (isInbound) {
      // Server-side hold path (see hold() above for full rationale).
      // Deafen receiver but KEEP SENDER ENABLED so RTP keeps flowing
      // to Asterisk (prevents chan_sip 31s RTP timeout).
      console.log('[CX-MC] parkCurrent: server-side hold (inbound)',
                  { parkedId, alreadyOnServerHold });
      try {
        const pc = parkedSession.connection;
        if (pc?.getReceivers) {
          for (const r of pc.getReceivers()) if (r.track) r.track.enabled = false;
        }
        // Sender track intentionally left enabled.
      } catch (_) {}
      if (!alreadyOnServerHold) {
        softphoneApi.holdStart().catch(err =>
          console.warn('[CX-MC] parkCurrent: holdStart threw', err));
      }
    } else {
      // Outbound / consult / non-queue: SIP hold as before.
      console.log('[CX-MC] parkCurrent: SIP hold (non-inbound)', parkedId);
      _safeHold(parkedSession);
    }

    setHeldCalls(prev => [
      ...prev,
      {
        id: parkedId,
        session: parkedSession,
        callInfo: parkedInfo || null,
        direction: parkedInfo?.direction || 'unknown',
        holdMode: isInbound ? 'server' : 'sip',
        heldAt: Date.now(),
      },
    ]);
    // Detach from the active slot WITHOUT terminating.
    session.current = null;
    currentSessionIdRef.current = null;
    setCallState(CallState.IDLE);
    setCallInfo(null);
    setIsLocalHold(false);
    setIsRemoteHold(false);
    console.log('[CX-MC] parked call', parkedId);
    return { id: parkedId };
  }, [callState]);

  // resumeHeld(id): promote a held session back to active. If there's
  // currently an active call, park it first (auto-swap).
  //
  // 2026-07-23: mirrors parkCurrent — if the held call was parked via
  // server-side hold (inbound queue call), resume it via backend
  // StopMOH + re-enable local tracks. Otherwise use SIP unhold.
  const resumeHeld = useCallback((id) => {
    const held = heldCallsRef.current.find(h => h.id === id);
    if (!held) {
      console.warn('[CX-MC] resumeHeld: id not found', id);
      return;
    }
    // Park current if any (swap).
    if (session.current && callState !== CallState.IDLE) {
      parkCurrent();
    }
    // Promote.
    session.current = held.session;
    currentSessionIdRef.current = held.id;
    setCallInfo(held.callInfo);
    setCallState(CallState.CONNECTED);
    setIsLocalHold(false);
    setIsRemoteHold(false);

    if (held.holdMode === 'server') {
      // Server-side unhold: backend re-bridges caller+agent, we just
      // re-enable local tracks. No SIP re-INVITE.
      console.log('[CX-MC] resumeHeld: server-side unhold', id);
      softphoneApi.holdStop().catch(err =>
        console.warn('[CX-MC] resumeHeld: holdStop threw', err));
      try {
        const pc = held.session.connection;
        if (pc?.getSenders) {
          for (const snd of pc.getSenders()) if (snd.track) snd.track.enabled = true;
        }
        if (pc?.getReceivers) {
          for (const r of pc.getReceivers()) if (r.track) r.track.enabled = true;
        }
      } catch (_) {}
      // 2026-07-23: after returning from a consult, Asterisk's Bridge()
      // switches the RTP source going toward the browser from MOH →
      // caller. rtpengine sees the source-side SSRC change and passes
      // through. Chrome may fire a fresh `ontrack` and the old audio
      // element MediaStream references stale receiver tracks. Reattach
      // remote audio to make sure the fresh incoming stream is what
      // plays.
      _reattachRemoteAudio(held.session);
    } else {
      // SIP unhold (outbound / consult / legacy).
      console.log('[CX-MC] resumeHeld: SIP unhold', id);
      _safeUnhold(held.session);
      _reattachRemoteAudio(held.session);
    }

    setHeldCalls(prev => prev.filter(h => h.id !== id));
    console.log('[CX-MC] resumed call', id);
  }, [callState, parkCurrent]);

  // hangupHeld(id): terminate a held session without resuming it first.
  const hangupHeld = useCallback((id) => {
    const held = heldCallsRef.current.find(h => h.id === id);
    if (!held) return;
    try { held.session.terminate(); } catch (err) {
      console.warn('[CX-MC] hangupHeld terminate threw', err);
    }
    setHeldCalls(prev => prev.filter(h => h.id !== id));
    console.log('[CX-MC] hung up held call', id);
  }, []);

  // startNewCall(): parks current then leaves state IDLE so the UI dialer
  // is available. If there's no active call, no-op (dialer already visible).
  const startNewCall = useCallback(() => {
    if (session.current && callState !== CallState.IDLE) {
      parkCurrent();
    }
  }, [callState, parkCurrent]);

  // acceptParkedIncoming(): accept the 2nd incoming call. Parks the active
  // one first, then answers.
  const acceptParkedIncoming = useCallback(() => {
    const pi = parkedIncoming;
    if (!pi) return;
    if (session.current && callState !== CallState.IDLE) {
      parkCurrent();
    }
    // Promote the parked incoming to the active slot.
    session.current = pi.session;
    currentSessionIdRef.current = pi.id;
    setCallInfo(pi.callInfo);
    setCallState(CallState.INCOMING);
    // Wire the normal session handlers now that it's the primary.
    setupSessionHandlers(pi.session);
    setParkedIncoming(null);
    // Now actually pick up.
    try {
      const opts = {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: [] },
      };
      pi.session.answer(opts);
      // Engagement model (2026-07-27): accepting a 2nd concurrent call was
      // previously MISSING a screen-recording trigger. A parked-incoming
      // accept is a NEW user call -> its own recording (per Fernando: one
      // file per call). If a recording is already active for the first
      // call, that call's recording is on hold in the engagement; the
      // coordinator treats this as a new user leg. Because the first call
      // is parked (still a live leg), the engagement set is non-empty, so
      // no wrap tail is active and this leg simply joins the engagement.
      // NOTE: with concurrent calls, both share ONE engagement recording by
      // design (the recorder captures the whole screen regardless of which
      // call is focused). See MODEL doc Q1 note on concurrency.
      (() => {
        const sr = window.__cxScreenRec;
        if (!sr) return;
        console.log('[CX-SR] parked-incoming legLive');
        sr.legLive(pi.session, {
          origin: 'user',
          callId: null,
          agentExtension: (lastCreds.current && lastCreds.current.extension) || null,
        });
      })();
    } catch (err) {
      console.error('[CX-MC] parked-incoming answer failed', err);
    }
  }, [parkedIncoming, callState, parkCurrent]);

  // rejectParkedIncoming(): send 486 to the 2nd incoming call, leave the
  // active call untouched.
  const rejectParkedIncoming = useCallback(() => {
    const pi = parkedIncoming;
    if (!pi) return;
    try {
      pi.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
    } catch (_) {}
    setParkedIncoming(null);
  }, [parkedIncoming]);
  
  // Mute / unmute apply to BOTH the primary session and the consult session
  // when a consult is in flight (attended transfer / conference flow).
  // During consult the local audio is flowing through consultSession, not
  // session.current (which holds the original caller, now on hold). Without
  // this dual-mute, clicking Mute during a consult silently fails because
  // it mutes the held caller leg (already on MOH) while leaving the active
  // consult leg fully audible. See useSIP.js mute-during-consult bug 2026-06-21.
  const mute = useCallback(() => {
    if (session.current) {
      try { session.current.mute({ audio: true }); } catch (_) {}
    }
    if (consultSession) {
      try { consultSession.mute({ audio: true }); } catch (_) {}
    }
    // Optimistic UI: if neither session fires 'muted' (e.g. JsSIP swallows
    // the event on the consult leg because we never wired it via
    // setupSessionHandlers), reflect intent in local state immediately.
    setIsMuted(true);
  }, [consultSession]);
  
  const unmute = useCallback(() => {
    if (session.current) {
      try { session.current.unmute({ audio: true }); } catch (_) {}
    }
    if (consultSession) {
      try { consultSession.unmute({ audio: true }); } catch (_) {}
    }
    setIsMuted(false);
    // 2026-07-23 server-side hold: if the call is currently on hold, our
    // custom hold() disabled the receiver tracks locally so the agent
    // doesn't hear the caller. JsSIP's unmute() doesn't touch receivers,
    // but if anything else re-enabled the receiver, we re-deafen here so
    // hold semantics survive a mute/unmute cycle. Sender track stays
    // enabled throughout hold (browser must keep sending RTP so
    // Asterisk's chan_sip doesn't 31s-timeout the agent leg).
    if (isLocalHold && session.current) {
      try {
        const pc = session.current.connection;
        if (pc?.getReceivers) {
          for (const r of pc.getReceivers()) {
            if (r.track) r.track.enabled = false;
          }
        }
      } catch (_) { /* best-effort */ }
    }
  }, [consultSession, isLocalHold]);
  
  const toggleMute = useCallback(() => {
    if (isMuted) {
      unmute();
    } else {
      mute();
    }
  }, [isMuted, mute, unmute]);
  
  const sendDTMF = useCallback((digit) => {
    if (session.current && callState === CallState.CONNECTED) {
      session.current.sendDTMF(digit);
    }
  }, [callState]);
  
  // ═══════════════════════════════════════════════════════════════
  // TRANSFER
  // ═══════════════════════════════════════════════════════════════
  
  // ----------------------------------------------------------------
  // Blind transfer (REFER without Replaces)
  //
  // We send REFER to Asterisk and DO NOT tear down the agent's session
  // until the NOTIFY confirms the far-side bridged successfully.
  //
  //   REFER -> 202 Accepted          => Asterisk took it
  //   NOTIFY sipfrag 1xx             => target is ringing
  //   NOTIFY sipfrag 2xx             => target answered => we can hang up
  //   NOTIFY sipfrag 4xx/5xx/6xx     => transfer failed => stay on call
  //
  // JsSIP exposes this via the ReferSubscriber returned by session.refer():
  //   .on('requestSucceeded') - REFER itself accepted (202)
  //   .on('progress')         - NOTIFY 1xx (target ringing)
  //   .on('accepted')         - NOTIFY 2xx (target answered)
  //   .on('failed')           - NOTIFY 4xx/5xx/6xx OR request error
  // ----------------------------------------------------------------
  const blindTransfer = useCallback((target) => {
    if (!session.current || callState !== CallState.CONNECTED) return;
    if (!target || !String(target).trim()) {
      setError('Enter a transfer target.');
      return;
    }
    const targetUri = `sip:${String(target).trim()}@${currentServer.sipDomain}`;
    console.log('[CX] blindTransfer:', targetUri);
    setTransferState({ type: TransferType.BLIND, target, step: 'requested' });
    setCallState(CallState.TRANSFERRING);

    let subscriber;
    try {
      subscriber = session.current.refer(targetUri);
    } catch (err) {
      console.error('[CX] blindTransfer: refer() threw', err);
      setError(`Transfer failed: ${err.message || err}`);
      setCallState(CallState.CONNECTED);
      setTransferState(null);
      return;
    }
    if (!subscriber) {
      console.error('[CX] blindTransfer: refer() returned null (session not confirmed?)');
      setError('Transfer failed: session not ready');
      setCallState(CallState.CONNECTED);
      setTransferState(null);
      return;
    }

    subscriber.on('requestSucceeded', () => {
      console.log('[CX] blindTransfer: REFER 202 Accepted by Asterisk');
      setTransferState((s) => s ? { ...s, step: 'accepted' } : s);
    });
    subscriber.on('progress', (ev) => {
      console.log('[CX] blindTransfer: NOTIFY progress', ev?.response?.status_code);
      setTransferState((s) => s ? { ...s, step: 'ringing' } : s);
    });
    subscriber.on('accepted', (ev) => {
      const code = ev?.response?.status_code;
      console.log('[CX] blindTransfer: NOTIFY accepted (final 2xx)', code);
      // Target picked up. Asterisk will bridge caller<->target. We can
      // safely terminate our own leg now.
      setTransferState((s) => s ? { ...s, step: 'completed' } : s);
      try { session.current?.terminate(); } catch (_) {}
      // Session-ended handler resets state to IDLE.
    });
    subscriber.on('failed', (ev) => {
      const code = ev?.response?.status_code;
      const reason = ev?.response?.reason_phrase || ev?.cause || 'unknown';
      console.warn('[CX] blindTransfer: failed', code, reason);
      setError(`Transfer failed: ${code ? code + ' ' : ''}${reason}`);
      // Stay connected to caller.
      setCallState(CallState.CONNECTED);
      setTransferState(null);
    });
    subscriber.on('requestFailed', (ev) => {
      const cause = ev?.cause || 'unknown';
      console.warn('[CX] blindTransfer: REFER request failed', cause);
      setError(`Transfer failed: ${cause}`);
      setCallState(CallState.CONNECTED);
      setTransferState(null);
    });
  }, [callState, currentServer]);
  
  // ----------------------------------------------------------------
  // Attended transfer
  //
  // Flow:
  //   1. Caller (session A) is put on hold via re-INVITE sendonly
  //   2. Browser places a 2nd outbound call to 3rd party (session B = consultSession)
  //   3. Agent talks to 3rd party privately
  //   4. Agent clicks Complete:
  //        REFER on session A with Refer-To: <consult URI?Replaces=<B's dialog id>>
  //   5. Asterisk swaps: A's far side (caller) is bridged to B's far side
  //      (3rd party), both browser sessions tear down naturally.
  //
  // Cancel before Complete: terminate B, unhold A, back to CONNECTED.
  // 3rd-party hangs up before Complete: B ends naturally; unhold A.
  // ----------------------------------------------------------------
  const attendedTransfer = useCallback(async (target) => {
    if (!session.current || callState !== CallState.CONNECTED) return;
    if (!target || !String(target).trim()) {
      setError('Enter a transfer target.');
      return;
    }

    // 1. Hold the caller (session A).
    try {
      session.current.hold();
    } catch (e) {
      console.warn('[CX] attended: hold() threw, continuing anyway', e);
    }

    // 2026-07-15 pm fix (MOH bleed during consult):
    // Even after session.current.hold() sends the re-INVITE, Asterisk
    // keeps pumping MOH-related RTP back to the agent for a short
    // window while the transaction settles. If that RTP hits the shared
    // <audio> element the consult agent hears MOH mixed with their voice.
    // Explicitly disable the caller-leg receivers so no RTP from that
    // session can reach the audio element until we come back off consult.
    try {
      const pcA = session.current.connection;
      if (pcA?.getReceivers) {
        for (const r of pcA.getReceivers()) {
          if (r.track) { r.track.enabled = false; }
        }
      }
    } catch (e) {
      console.warn('[CX] attended: could not disable caller receivers', e);
    }

    setTransferState({ type: TransferType.ATTENDED, target, step: 'consulting' });
    setCallState(CallState.TRANSFERRING);

    // 2. Place the consult call to the 3rd party.
    const consultUri = `sip:${String(target).trim()}@${currentServer.sipDomain}`;
    console.log('[CX] attended: dialing consult', consultUri);

    const consultOptions = {
      mediaConstraints: {
        audio: selectedInput
          ? { deviceId: { exact: selectedInput } }
          : true,
        video: false,
      },
      pcConfig: {
        iceServers: (CONFIG.ice.stunServers || []).map(url => ({ urls: url })),
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      },
    };

    let consultCall;
    try {
      consultCall = ua.current.call(consultUri, consultOptions);
    } catch (err) {
      console.error('[CX] attended: ua.call() threw', err);
      setError(`Consult call failed: ${err.message || err}`);
      try { session.current.unhold(); } catch (_) {}
      setCallState(CallState.CONNECTED);
      setTransferState(null);
      return;
    }
    if (!consultCall) {
      console.error('[CX] attended: ua.call() returned null');
      setError('Consult call failed: UA returned no session');
      try { session.current.unhold(); } catch (_) {}
      setCallState(CallState.CONNECTED);
      setTransferState(null);
      return;
    }

    setConsultSession(consultCall);

    // Engagement model (2026-07-27): the consult leg is a CONTINUATION of the
    // ongoing interaction, not a new call. Report it live so it joins the
    // engagement (keeps the same recording rolling). It does NOT flow
    // through setupSessionHandlers, so we also attach a legGone reporter to
    // its own ended/failed below.
    (() => {
      const sr = window.__cxScreenRec;
      if (!sr || !sr.legLive) return;
      console.log('[CX-SR] attended consult legLive (continuation)');
      sr.legLive(consultCall, { origin: 'continuation' });
      const reportGone = () => { try { sr.legGone(consultCall); } catch (_) {} };
      consultCall.on('ended', reportGone);
      consultCall.on('failed', reportGone);
    })();

    // Wire ONLY the audio for the consult call. We deliberately do NOT call
    // setupSessionHandlers() because it mutates global state (setCallState,
    // start timers, etc.) which would clobber our TRANSFERRING state.
    const wireConsultAudio = (pc) => {
      if (!pc) return;
      pc.ontrack = (event) => {
        try {
          if (!remoteAudio.current) {
            remoteAudio.current = new Audio();
            remoteAudio.current.autoplay = true;
            remoteAudio.current.id = 'cx-remote-audio';
            try { document.body.appendChild(remoteAudio.current); } catch (_) {}
          }
          const stream = event.streams && event.streams[0];
          if (stream) {
            remoteAudio.current.srcObject = stream;
          } else if (event.track) {
            remoteAudio.current.srcObject = new MediaStream([event.track]);
          }
          const p = remoteAudio.current.play();
          if (p && p.catch) p.catch(() => {});
          if (selectedOutput && remoteAudio.current.setSinkId) {
            remoteAudio.current.setSinkId(selectedOutput).catch(() => {});
          }
        } catch (e) {
          console.error('[CX] attended: consult ontrack failed', e);
        }
      };
    };
    consultCall.on('peerconnection', (e) => {
      console.log('[CX] attended: consult peerconnection ready');
      wireConsultAudio(e.peerconnection);
    });
    if (consultCall.connection) wireConsultAudio(consultCall.connection);

    consultCall.on('confirmed', () => {
      console.log('[CX] attended: consult confirmed (3rd party answered)');
      setTransferState((s) => s ? { ...s, step: 'connected' } : s);
    });
    // Re-enable the caller receivers so audio comes back once the
    // consult ends. Extracted so both `failed` and `ended` paths use it.
    const reenableCallerReceivers = () => {
      try {
        const pcA = session.current?.connection;
        if (pcA?.getReceivers) {
          for (const r of pcA.getReceivers()) {
            if (r.track) { r.track.enabled = true; }
          }
        }
      } catch (_) { /* swallow */ }
    };

    // 2026-07-15 pm mitigation for the caller auto-disconnect during
    // long consults. While the caller is on hold + we're consulting,
    // some downstream RTP-inactivity detectors (rtpengine, Asterisk,
    // upstream carrier) drop the leg after ~30-60s of silence. Send a
    // periodic SIP OPTIONS ping down the caller dialog to keep the
    // dialog alive (RFC 5626 keepalive). This is a hack; the real fix
    // is a server-side MusicOnHold() via AMI so RTP keeps flowing.
    // TODO(2026-07-16): move to AMI-driven MOH.
    const consultKeepaliveIv = setInterval(() => {
      try {
        if (!session.current) return;
        const dlg = session.current._dialog || session.current.dialog;
        if (!dlg) return;
        // JsSIP doesn't expose a public in-dialog OPTIONS; fall back to
        // an in-dialog INFO with a no-op body which most stacks accept
        // and which does nothing to the media but resets keepalive
        // timers on Kamailio + rtpengine.
        if (typeof session.current.sendInfo === 'function') {
          session.current.sendInfo('application/x-cx-keepalive', '', {});
        }
      } catch (e) {
        console.warn('[CX] attended: keepalive INFO failed', e);
      }
    }, 25_000);

    // Cancel keepalive when consult ends via any of the paths below.
    const cancelKeepalive = () => {
      try { clearInterval(consultKeepaliveIv); } catch (_) {}
    };

    consultCall.on('failed', (ev) => {
      const cause = ev?.cause || 'unknown';
      console.warn('[CX] attended: consult call failed', cause);
      setError(`Consult call failed: ${cause}`);
      cancelKeepalive();
      reenableCallerReceivers();
      try { session.current?.unhold(); } catch (_) {}
      // Re-attach the caller audio (re-INVITE brings tracks back live).
      if (session.current) _reattachRemoteAudio(session.current);
      setCallState(CallState.CONNECTED);
      setTransferState(null);
      setConsultSession(null);
    });
    consultCall.on('ended', () => {
      console.log('[CX] attended: consult ended');
      cancelKeepalive();
      setTransferState((s) => {
        if (s?.step !== 'completed' && session.current) {
          reenableCallerReceivers();
          try { session.current.unhold(); } catch (_) {}
          if (session.current) _reattachRemoteAudio(session.current);
          setCallState(CallState.CONNECTED);
          return null;
        }
        return s;
      });
      setConsultSession(null);
    });
  }, [callState, currentServer, selectedInput]);

  const completeAttendedTransfer = useCallback(() => {
    if (!session.current || !consultSession) {
      setError('No consult call to complete.');
      return;
    }
    // Build the Refer-To URI for the 3rd party (target of the consult call).
    // Use toAor() to get the canonical sip:user@domain without dialog params.
    let targetAor;
    try {
      targetAor = consultSession.remote_identity.uri.toAor();
    } catch (e) {
      targetAor = consultSession.remote_identity.uri.toString();
    }
    console.log('[CX] attended: REFER+Replaces, target=', targetAor);
    try {
      const sub = session.current.refer(targetAor, {
        replaces: consultSession,
      });
      if (!sub) {
        throw new Error('refer() returned null subscriber');
      }
      setTransferState((s) => s ? { ...s, step: 'completed' } : s);
      // Wait for accepted/failed NOTIFY. Asterisk will hang up our leg.
      sub.on('accepted', () => {
        console.log('[CX] attended: NOTIFY accepted - far side bridged');
        // Tear down both browser sessions now (caller leg + consult leg).
        try { consultSession.terminate(); } catch (_) {}
        try { session.current?.terminate(); } catch (_) {}
      });
      sub.on('failed', (ev) => {
        const code = ev?.response?.status_code;
        const reason = ev?.response?.reason_phrase || ev?.cause || 'unknown';
        console.warn('[CX] attended: REFER failed', code, reason);
        setError(`Transfer failed: ${code ? code + ' ' : ''}${reason}`);
        setTransferState((s) => s ? { ...s, step: 'connected' } : s);
      });
    } catch (err) {
      console.error('[CX] attended: completeAttendedTransfer threw', err);
      setError(`Transfer failed: ${err.message || err}`);
    }
  }, [consultSession]);

  const cancelTransfer = useCallback(() => {
    console.log('[CX] cancelTransfer invoked');
    if (consultSession) {
      try { consultSession.terminate(); } catch (_) {}
      setConsultSession(null);
    }
    if (session.current) {
      try { session.current.unhold(); } catch (_) {}
    }
    setTransferState(null);
    setCallState(CallState.CONNECTED);
  }, [consultSession]);
  
  // ═══════════════════════════════════════════════════════════════
  // CONFERENCE (3-WAY via Asterisk ConfBridge)
  //
  // startConference:
  //   1. Hold session A (caller)
  //   2. Place 2nd outbound call to 3rd party (session B = consultSession)
  //   3. Agent talks to 3rd party privately. CallState = CONFERENCING.
  //
  // mergeConference:
  //   1. POST /api/softphone/conference/merge
  //   2. Backend uses AMI Redirect to flip all 4 channels (agent's 2 SIP
  //      legs + caller's far side + 3rd-party's far side) into a fresh
  //      ConfBridge room. Audio mixes server-side.
  //   3. Browser stays in CONFERENCING state. Backend returns room id.
  //
  // releaseConference:
  //   1. POST /api/softphone/conference/release
  //   2. Backend AMI Hangup on the admin (agent) channels only. ConfBridge
  //      cx_admin profile has end_marked=no -> other 2 stay connected.
  //   3. Browser receives BYE on both sessions, transitions to IDLE.
  //
  // endAllConference:
  //   - Just terminate both browser sessions. Asterisk tears down the
  //     bridge, all 3 hang up.
  // ═══════════════════════════════════════════════════════════════

  // 2026-07-26: Add Call helper. Puts the caller on MOH (SIP hold) IMMEDIATELY
  // when the agent clicks Add Call, so they hear music while the agent types
  // the 3rd-party number — rather than only when Call is pressed. This SIP
  // hold is the exact same one startConference() uses, so the subsequent
  // startConference() call skips re-holding (guarded by alreadyHeldForConf).
  const alreadyHeldForConfRef = useRef(false);
  const holdCallerForAddCall = useCallback(() => {
    if (!session.current) return;
    try {
      session.current.hold();
      alreadyHeldForConfRef.current = true;
      // Disable caller-leg receivers so the agent doesn't hear MOH bleed
      // while composing the number (mirrors startConference/attendedTransfer).
      const pcA = session.current.connection;
      if (pcA?.getReceivers) {
        for (const r of pcA.getReceivers()) {
          if (r.track) { r.track.enabled = false; }
        }
      }
      console.log('[CX] Add Call: caller SIP-held to MOH immediately');
    } catch (e) {
      console.warn('[CX] Add Call: holdCallerForAddCall threw', e);
    }
  }, []);

  // Cancel path for Add Call: bring the caller back off the SIP hold that
  // holdCallerForAddCall applied, and re-enable their audio.
  const resumeFromAddCall = useCallback(() => {
    if (!session.current) return;
    alreadyHeldForConfRef.current = false;
    try {
      session.current.unhold();
      const pcA = session.current.connection;
      if (pcA?.getReceivers) {
        for (const r of pcA.getReceivers()) {
          if (r.track) { r.track.enabled = true; }
        }
      }
      _reattachRemoteAudio(session.current);
      console.log('[CX] Add Call: cancelled, caller resumed off MOH');
    } catch (e) {
      console.warn('[CX] Add Call: resumeFromAddCall threw', e);
    }
  }, []);

  const startConference = useCallback(async (target) => {
    if (!session.current
        || (callState !== CallState.CONNECTED
            && callState !== CallState.CONFERENCING
            && callState !== CallState.HOLDING)) return;
    if (!target || !String(target).trim()) {
      setError('Enter a conference target.');
      return;
    }

    // Skip re-holding if the caller was already SIP-held by holdCallerForAddCall
    // (Add Call flow). Otherwise hold now (classic Conference-button flow).
    if (!alreadyHeldForConfRef.current) {
      try { session.current.hold(); } catch (_) {}
    }
    alreadyHeldForConfRef.current = false;
    setCallState(CallState.CONFERENCING);
    conferenceMergedRef.current = false;
    setConferenceMerged(false);
    setConferenceRoom(null);

    const consultUri = `sip:${String(target).trim()}@${currentServer.sipDomain}`;
    console.log('[CX] conference: dialing 3rd party', consultUri);

    const confOptions = {
      mediaConstraints: {
        audio: selectedInput
          ? { deviceId: { exact: selectedInput } }
          : true,
        video: false,
      },
      pcConfig: {
        iceServers: (CONFIG.ice.stunServers || []).map(url => ({ urls: url })),
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      },
    };

    let confCall;
    try {
      confCall = ua.current.call(consultUri, confOptions);
    } catch (err) {
      console.error('[CX] conference: ua.call() threw', err);
      setError(`Conference dial failed: ${err.message || err}`);
      try { session.current.unhold(); } catch (_) {}
      setCallState(CallState.CONNECTED);
      return;
    }
    if (!confCall) {
      setError('Conference dial failed: UA returned no session');
      try { session.current.unhold(); } catch (_) {}
      setCallState(CallState.CONNECTED);
      return;
    }

    setConsultSession(confCall);

    // Engagement model (2026-07-27): conference consult leg is a CONTINUATION.
    (() => {
      const sr = window.__cxScreenRec;
      if (!sr || !sr.legLive) return;
      console.log('[CX-SR] conference consult legLive (continuation)');
      sr.legLive(confCall, { origin: 'continuation' });
      const reportGone = () => { try { sr.legGone(confCall); } catch (_) {} };
      confCall.on('ended', reportGone);
      confCall.on('failed', reportGone);
    })();

    // Wire only audio (don't call setupSessionHandlers - it mutates global state).
    const wireConsultAudio = (pc) => {
      if (!pc) return;
      pc.ontrack = (event) => {
        try {
          if (!remoteAudio.current) {
            remoteAudio.current = new Audio();
            remoteAudio.current.autoplay = true;
            remoteAudio.current.id = 'cx-remote-audio';
            try { document.body.appendChild(remoteAudio.current); } catch (_) {}
          }
          const stream = event.streams && event.streams[0];
          if (stream) remoteAudio.current.srcObject = stream;
          else if (event.track) remoteAudio.current.srcObject = new MediaStream([event.track]);
          const p = remoteAudio.current.play();
          if (p && p.catch) p.catch(() => {});
          if (selectedOutput && remoteAudio.current.setSinkId) {
            remoteAudio.current.setSinkId(selectedOutput).catch(() => {});
          }
        } catch (e) {
          console.error('[CX] conference: consult ontrack failed', e);
        }
      };
    };
    confCall.on('peerconnection', (e) => wireConsultAudio(e.peerconnection));
    if (confCall.connection) wireConsultAudio(confCall.connection);

    confCall.on('confirmed', () => {
      console.log('[CX] conference: 3rd party answered');
    });
    confCall.on('failed', (ev) => {
      const cause = ev?.cause || 'unknown';
      console.warn('[CX] conference: consult call failed', cause);
      setError(`3rd party did not answer: ${cause}`);
      try { session.current?.unhold(); } catch (_) {}
      setCallState(CallState.CONNECTED);
      setConsultSession(null);
      conferenceMergedRef.current = false;
      setConferenceMerged(false);
      setConferenceRoom(null);
    });
    confCall.on('ended', () => {
      const merged = conferenceMergedRef.current;
      console.log('[CX] conference: consult ended (merged=', merged, ')');
      if (!merged) {
        // Genuine consult drop (3rd party hung up before merge, or agent
        // cancelled). Bring caller back off hold.
        try { session.current?.unhold(); } catch (_) {}
        setCallState(CallState.CONNECTED);
        setConsultSession(null);
      } else {
        // Merged: backend hung up our consult-leg session B as part of
        // moving the 3rd party into ConfBridge. Just clear the ref so
        // CallControls can render the post-merge state. Audio will
        // continue via the NEW incoming auto-answered call from backend.
        setConsultSession(null);
      }
    });
  }, [callState, currentServer, selectedInput]);

  const mergeConference = useCallback(async () => {
    if (!session.current || !consultSession) {
      setError('No consult call to merge.');
      return;
    }
    console.log('[CX] conference: requesting merge from backend (originate flow)');
    // CRITICAL: set the merged ref BEFORE the request fires, so when the
    // backend hangs up session B and the on('ended') handler runs, it sees
    // merged=true and doesn't try to unhold session A.
    conferenceMergedRef.current = true;
    setConferenceMerged(true);
    conferenceMergeWindowUntil.current = Date.now() + 15000; // conf-join arrives ~1-2s; generous window
    console.log('[CX-WRAP-TRIGGER] mergeConference set conferenceMergedRef=true');
    try {
      const resp = await softphoneApi.conferenceMerge();
      console.log('[CX] conference: merge response', resp);
      if (!resp || !resp.ok) {
        // Roll back the merged flag - we're staying in the consult state.
        conferenceMergedRef.current = false;
        setConferenceMerged(false);
        // COSMETIC (2026-07-29): the `missing_pstn_legs` / `missing_pstn_partners`
        // reason is a benign backend leg-detection race that fires even when the
        // conference actually established fine (confirmed by agents on the
        // inbound->911 conference flow). Do NOT surface it to the agent; keep it
        // in the console for debugging. Other genuine merge failures still show.
        if (_isBenignMergeReason(resp?.reason)) {
          console.warn('[CX] mergeConference: suppressed benign merge reason', resp?.reason);
        } else {
          setError(`Merge failed: ${resp?.reason || 'unknown'}`);
        }
        return;
      }
      setConferenceRoom(resp.room);
      // Backend has:
      //  1. moved caller + 3rd party into ConfBridge
      //  2. hung up old session A (caller leg) + session B (consult leg)
      //  3. armed auto-answer for the agent
      //  4. Originated a NEW call to agent's extension that will arrive
      //     in ~1-2 seconds; auto-answered by useSIP's incoming-queue-call
      //     handler; lands in ConfBridge as admin.
      // Browser sessions A + B will end via BYE. Their on('ended') sees
      // conferenceMergedRef.current === true and skips auto-unhold.
      // The NEW incoming session becomes session.current.
      //
      // Force CallState to CONFERENCING so the Release button shows up
      // (CallControls keys off callState + conferenceMerged regardless of
      // whether session.current is the old or new leg).
      setCallState(CallState.CONFERENCING);
      // Clear consult reference; session B is dying.
      setConsultSession(null);
    } catch (err) {
      conferenceMergedRef.current = false;
      setConferenceMerged(false);
      console.error('[CX] conference: mergeConference threw', err);
      setError(`Merge failed: ${err.message || err}`);
    }
  }, [consultSession]);

  // MC-1.5 (2026-07-02): merge held + active into a 3-way conference.
  // Works when the agent has exactly 1 active + 1 held call.
  const mergeHeldAndActive = useCallback(async () => {
    if (!session.current || heldCallsRef.current.length !== 1) {
      setError('Need exactly one active call and one held call to merge.');
      return;
    }
    console.log('[CX] mergeHeldAndActive: requesting backend merge');
    // CRITICAL (2026-07-02 fix): un-hold the held session BEFORE merging.
    // If we leave it on SIP hold (sendonly/recvonly re-INVITE from park),
    // Asterisk redirects the held-state LOCAL peer into ConfBridge and
    // the media path is broken — one caller can't hear the other. The
    // un-hold re-INVITE renegotiates SDP back to sendrecv on that leg
    // BEFORE ConfBridge takes over.
    try {
      const held = heldCallsRef.current[0];
      if (held && held.session) {
        held.session.unhold();
        // Give the re-INVITE ~200ms to complete SDP round-trip before
        // the backend snapshots CoreShowChannels.
        await new Promise((r) => setTimeout(r, 250));
      }
    } catch (err) {
      console.warn('[CX] mergeHeldAndActive: unhold-before-merge threw', err);
      // Non-fatal — continue with merge; worst case old audio bug returns
      // on this one call.
    }
    // Same as mergeConference: flag merged BEFORE the request fires so
    // that when both sessions get BYE from Asterisk their on("ended")
    // handlers see merged=true and skip auto-unhold / wrap-up dispatch.
    conferenceMergedRef.current = true;
    setConferenceMerged(true);
    conferenceMergeWindowUntil.current = Date.now() + 15000;
    try {
      const resp = await softphoneApi.conferenceMergeHeld();
      console.log('[CX] mergeHeldAndActive: response', resp);
      if (!resp || !resp.ok) {
        conferenceMergedRef.current = false;
        setConferenceMerged(false);
        // COSMETIC (2026-07-29): suppress the benign leg-detection-race reason
        // (see mergeConference note). Conference still establishes fine.
        if (_isBenignMergeReason(resp?.reason)) {
          console.warn('[CX] mergeHeldAndActive: suppressed benign merge reason', resp?.reason);
        } else {
          setError(`Merge held failed: ${resp?.reason || 'unknown'}`);
        }
        return;
      }
      setConferenceRoom(resp.room);
      // Backend has:
      //  1. redirected both PSTN legs into ConfBridge
      //  2. hung up both agent SIP legs (active + held)
      //  3. armed auto-answer for the agent
      //  4. Originated a NEW call to agent that will arrive ~1-2s later
      //     and be auto-answered into ConfBridge as admin.
      // Clear held tray + primary slot; they'll finish tearing down
      // via BYE on their own but the UI shouldn't offer resume for them.
      setHeldCalls([]);
      setCallState(CallState.CONFERENCING);
    } catch (err) {
      conferenceMergedRef.current = false;
      setConferenceMerged(false);
      console.error('[CX] mergeHeldAndActive threw', err);
      setError(`Merge held failed: ${err.message || err}`);
    }
  }, []);

  // 2026-07-27: mark the softphone as being in a MERGED conference after the
  // server-side multi-line merge (lineMerge). Sets the flags so CallControls
  // shows "Release me" (wired to releaseConference) instead of "End Call".
  const markMerged = useCallback((room) => {
    conferenceMergedRef.current = true;
    setConferenceMerged(true);
    conferenceMergeWindowUntil.current = Date.now() + 15000;
    if (room) setConferenceRoom(room);
    setCallState(CallState.CONFERENCING);
  }, []);

  const releaseConference = useCallback(async () => {
    console.log('[CX] conference: requesting release from backend', conferenceRoom);
    try {
      const resp = await softphoneApi.conferenceRelease(conferenceRoom);
      console.log('[CX] conference: release response', resp);
      if (!resp || !resp.ok) {
        // Even if backend reports an issue, fall back to local hangup so
        // the agent isn't stuck.
        setError(`Release: ${resp?.reason || 'falling back to local hangup'}`);
      }
    } catch (err) {
      console.warn('[CX] conference: releaseConference threw', err);
    } finally {
      // Reset the merged flag BEFORE terminating so session-ended fires
      // wrap-up via its existing path (avoids dispatching the event
      // twice). The merge-time suppression in session.on('ended') uses
      // conferenceMergedRef.current; setting it false here means the
      // ended handler will see false and dispatch normally.
      conferenceMergedRef.current = false;
      setConferenceMerged(false);
      // Local tear-down: hang up the agent's current session.
      try { consultSession?.terminate(); } catch (_) {}
      try { session.current?.terminate(); } catch (_) {}
      setConsultSession(null);
      setConferenceRoom(null);
      setCallState(CallState.IDLE);
    }
  }, [conferenceRoom, consultSession]);

  // endAllConference: end the ENTIRE call (all parties) when the agent clicks
  // "End All" during a conference / merged multi-line call. The parties may be
  // in a server-side ConfBridge (not bridged to the browser leg), so a local
  // session.terminate() alone leaves them connected. We call the server-side
  // end-all first, then tear down locally and fire the normal wrap-up path.
  // 2026-07-28.
  const endAllConference = useCallback(async () => {
    console.log('[CX] conference: END ALL requested (server-side teardown)');
    try {
      const resp = await softphoneApi.lineEndAll();
      console.log('[CX] conference: end-all response', resp);
    } catch (err) {
      console.warn('[CX] conference: lineEndAll threw', err);
    }
    // Reset merged flag BEFORE terminating so the session-ended handler fires
    // the normal wrap-up path (it checks conferenceMergedRef to avoid double
    // dispatch during a merge transition).
    conferenceMergedRef.current = false;
    setConferenceMerged(false);
    try { consultSession?.terminate(); } catch (_) {}
    try { session.current?.terminate(); } catch (_) {}
    setConsultSession(null);
    setConferenceRoom(null);
    setCallState(CallState.IDLE);
    // Guarantee wrap-up fires even though this was a conference (the browser
    // may not emit a clean inbound 'session ended' for a ConfBridge leg).
    try {
      window.dispatchEvent(new CustomEvent('cx-call-ended-inbound', {
        detail: { reason: 'end-all' },
      }));
    } catch (_) {}
  }, [consultSession]);

  // Legacy alias - kept for old call sites that reference dropConferenceParty.
  const dropConferenceParty = useCallback((which) => {
    if (which === 'original' && session.current) {
      try { session.current.terminate(); } catch (_) {}
      session.current = consultSession;
      setConsultSession(null);
    } else if (which === 'added' && consultSession) {
      try { consultSession.terminate(); } catch (_) {}
      setConsultSession(null);
    }
    setCallState(CallState.CONNECTED);
  }, [consultSession]);
  
  // ═══════════════════════════════════════════════════════════════
  // TIMERS & MONITORING
  // ═══════════════════════════════════════════════════════════════
  
  const startDurationTimer = () => {
    durationTimer.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };
  
  const stopDurationTimer = () => {
    if (durationTimer.current) {
      clearInterval(durationTimer.current);
      durationTimer.current = null;
    }
  };
  
  const startQualityMonitoring = () => {
    qualityTimer.current = setInterval(async () => {
      if (!session.current?.connection) return;
      
      try {
        const stats = await session.current.connection.getStats();
        let packetsLost = 0;
        let packetsReceived = 0;
        // 2026-07-15 pm: Helen asked for both directions on the QoS
        // panel. `packetsSent` comes from the outbound-rtp report.
        let packetsSent = 0;
        let bytesReceived = 0;
        let bytesSent = 0;
        let jitter = 0;
        let roundTripTime = 0;

        stats.forEach(report => {
          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            packetsLost = report.packetsLost || 0;
            packetsReceived = report.packetsReceived || 0;
            bytesReceived = report.bytesReceived || 0;
            jitter = report.jitter || 0;
          }
          if (report.type === 'outbound-rtp' && report.kind === 'audio') {
            packetsSent = report.packetsSent || 0;
            bytesSent   = report.bytesSent   || 0;
          }
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            roundTripTime = report.currentRoundTripTime || 0;
          }
        });

        // Calculate MOS (simplified)
        const lossRate = packetsReceived > 0
          ? packetsLost / (packetsLost + packetsReceived)
          : 0;
        const effectiveLatency = roundTripTime * 1000 + jitter * 1000 + 10;
        let r = 93.2 - effectiveLatency / 40 - lossRate * 2.5 * 100;
        const mos = r < 0 ? 1 : 1 + 0.035 * r + 0.000007 * r * (r - 60) * (100 - r);

        setCallQuality({
          packetsLost,
          packetsReceived,
          packetsSent,
          bytesReceived,
          bytesSent,
          jitter: Math.round(jitter * 1000),
          roundTripTime: Math.round(roundTripTime * 1000),
          mos: Math.min(4.5, Math.max(1, mos)).toFixed(1)
        });
      } catch (err) {
        console.error('Failed to get call stats:', err);
      }
    }, 2000);
  };
  
  const stopQualityMonitoring = () => {
    if (qualityTimer.current) {
      clearInterval(qualityTimer.current);
      qualityTimer.current = null;
    }
  };
  
  // ═══════════════════════════════════════════════════════════════
  // RINGTONE
  // ═══════════════════════════════════════════════════════════════
  
  // Tone control - synthesized via Web Audio API, no file dependency.
  // ringStopFn is a closure returned by playIncomingRing/playRingback that
  // cleanly stops scheduled gain ramps + oscillators.
  const ringStopFn = useRef(null);

  const playRingtone = () => {
    if (!CONFIG.notifications.sound) return;
    stopRingtone();
    ringStopFn.current = playIncomingRing();
  };

  const playOutgoingRingback = () => {
    if (!CONFIG.notifications.sound) return;
    stopRingtone();
    ringStopFn.current = playRingback();
  };

  const stopRingtone = () => {
    if (ringStopFn.current) {
      try { ringStopFn.current(); } catch (e) { /* ignore */ }
      ringStopFn.current = null;
    }
  };
  
  // ═══════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════
  
  useEffect(() => {
    return () => {
      // Page unmount / hot-reload — NOT a user-initiated logout. We just tear
      // down resources; do not flip the intentional-logout flag (which would
      // suppress the next user's registrationFailed feedback).
      _teardown(false);
      stopDurationTimer();
      stopQualityMonitoring();
    };
  }, [_teardown]);
  
  // ═══════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════
  
  return {
    // Connection
    connectionState,
    currentServer,
    error,
    connect,
    disconnect,
    reconnect,
    switchCluster,
    didUserLogout,
    
    // Call state
    callState,
    callInfo,
    callDuration,
    callQuality,
    
    // Call controls
    call,
    answer,
    hangup,
    
    // Hold
    hold,
    unhold,
    isLocalHold,
    isRemoteHold,

    // Multi-call (MC-1) — held-tray API
    heldCalls,
    parkedIncoming,
    parkCurrent,
    resumeHeld,
    hangupHeld,
    startNewCall,
    acceptParkedIncoming,
    rejectParkedIncoming,
    maxConcurrentCalls: MAX_CONCURRENT_CALLS,
    // MC-1.5: merge held + active into 3-way conference (uses existing
    // releaseConference for the release-me step).
    mergeHeldAndActive,
    
    // Mute
    mute,
    unmute,
    toggleMute,
    isMuted,
    
    // DTMF
    sendDTMF,
    
    // Transfer
    blindTransfer,
    attendedTransfer,
    holdCallerForAddCall,
    resumeFromAddCall,
    completeAttendedTransfer,
    cancelTransfer,
    transferState,
    
    // Conference
    startConference,
    mergeConference,
    releaseConference,
    endAllConference,
    markMerged,
    dropConferenceParty,
    consultSession: !!consultSession,
    conferenceMerged,
    conferenceRoom,
    
    // Audio devices
    audioDevices,
    selectedInput,
    selectedOutput,
    setInputDevice,
    setOutputDevice,
    refreshAudioDevices
  };
}

export default useSIP;
